/* =====================================================================
   THE ROOM ENGINE — THE FIELD

   The record, turned into ONE height field over the whole strip, by the
   SAME arithmetic survey-map.js uses (v28: the record is one span). The
   old file is the SPEC, not the base: every constant and every step below
   is the one named in survey-map.js, and the line numbers are given so the
   two can be checked against each other. Nothing here touches the DOM, so
   it runs in a worker as happily as on the main thread.

   The output is the field on its OWN lattice — 5 x 3.5454 sheet units per
   cell — and the arithmetic that turns a sheet coordinate into a cell.
   The GL side samples that lattice directly (ROOM-PLAN §1: a half-cell
   phase error is a bilinear-of-bilinear resample that rounds every corner).
   ===================================================================== */
(function (root) {
  'use strict';

  /* ---- the constants, all from survey-map.js -------------------- */
  var C = {
    WEEK_START: 'sunday',
    BOUSTROPHEDON: true,
    SEQUENCE_SMOOTH: 0.22,      // :69
    BASELINE_COMMITS: 5,        // :84
    FRONT_FADE_DAYS: 2,         // :196
    CONJECTURE_KEEP: 0.78,      // :213
    EARLIEST_YEAR: 2022,        // :229
    EARLIEST_DATE: '2022-01-01',// :230
    EDGE_ROWS: 0,               // :243
    CONTEXT_WEEKS: 8,           // :252
    ux: 8, uy: 22, pad: 46, padY: 96,   // :1828
    /* ==== R26: THE TINT LADDER — THE ONE SOURCE ======================
       It stood in FOUR copies (field.js's rungs, engine.js's tintBand/
       tintCol, engine.js's tint-ramp texture, water.js's rBand/rTint for
       the beyond) and four copies of a ladder are four ladders. It is one
       here now, and the three shaders are WRITTEN from it the way
       ${EXAG} is written into them (TINT_GLSL below).

       IT IS ABSOLUTE, AND IT IS EXTENDED UPWARD, NOT RESCALED. v31's C1
       ruling: a day's colour means a number of commits and nothing else,
       so the historic record must not recolour as the record grows. The
       record's yearly maximum has gone 26 -> 35 -> 46 -> 71 -> 284
       (2022..2026) and only four days — all August 2026 — reach the old
       top rung, so on that trend next year's ORDINARY day wears the top
       brown and the sheet stops saying anything. Tommy, 2026-09-13:
       "we're not leaving ourself any space to grow?" and "i can live
       with 3x today's peak as the max-out commit number". So: rows 0..7
       are survey-map.js:1063's own, to the byte, and three rungs are
       added above in the same ~1.5x ratio, up to the ~850 the geometry
       is cut for.

       THE TWO BROWNS ARE NOT A TYPO. survey-map.js:1065-1069 clamps the
       band at 8 while handing it nine tones and eight rungs, so 125 and
       190 have ALWAYS shared the top brown and the 190 rung has never
       coloured anything. Keeping the duplicate keeps the sheet as it is
       printed today (and keeps the four August days in the brown they
       are in); the ladder's new work all happens above it. */
    TINT_AT: [8, 14, 22, 34, 52, 80, 125, 190, 300, 480, 760],  // :1063 + R26
    /* index 0 is the land's base wash; 1..12 are the bands, one per
       rung plus the ground under the first. The nine to #8d5238 are
       survey-map.js's .sv-tint--0..8 unchanged. */
    TINT_COL: ['#c5d0a1', '#d7d7a4', '#e6dcaa', '#eed89b', '#e5c281', '#d6a566',
               '#c4864e', '#ab6a3e', '#8d5238', '#8d5238',
               /* R26, past dark brown, the hypsometric convention's own
                  end: a darker umber, a grey-brown scree, and the snow
                  line — the one thing brown alone cannot do is give the
                  eye a TOP. THE GREY KEEPS THE SHEET'S WARMTH: of five
                  triples struck against the rungs below them, a neutral
                  grey (#9a8f85) read as concrete and a violet-grey
                  (#8f8790, the Times Atlas's own) read as a DIFFERENT
                  MAP where every other ink is sepia. The summit is
                  cooler than the paper (#f1e7d1) as well as lighter, so
                  a snowfield is not a hole in the printing. */
               '#74442e', '#948578', '#ece9e1'],
    /* the commits/day the ladder is cut to reach — Tommy's "3x today's
       peak", and the same number the camera's lift, the beyond and the
       section strip are sized to. It sets how far the tint loop walks. */
    TINT_TOP: 850,
    // the sheet's own geometry (geometry(), :1522)
    cw: 40, ch: 78, weeks: 53, rows: 7,
    px: 122, py: 158, band: 26, gap: 8,
    VERTICAL_EXAGGERATION: 20,  // relief.js:89
    MAX_TILT: 75 * Math.PI / 180 // relief.js:97
  };
  var DAY_MS = 86400000;

  /* ==== R70: THE SHEET SCALES TO ITS RECORD ==========================
     Tommy, on a stranger's sheet: "what about making the commit number
     scale dynamic since they have way less commits than me", and the
     ruling that followed: "we should adjust the relative scale for each
     handle entered". So there is no switch. THE SHEET'S SCALE IS THE
     SCALE OF THE RECORD ON IT.

     THE RULE, in one sentence: a record's own ordinary day stands where
     his ordinary day stands, and a record's own best day stands where
     his best day stands; everything between follows the same curve.

     THE ARITHMETIC. Every record hands up two numbers off its levelled
     days (the working days since the datum horizon): the median `m` and
     the highest `p`. The record filed with the page hands up `m0`, `p0`
     — and the sheet is PRINTED for that record, so those two are the
     ladder as it stands. A record that arrives over the wire is placed
     on that printed ladder by the power that carries its own two numbers
     on to his:

         sheet commits  c^ = m0 * (c / m) ^ B ,   B = ln(p0/m0) / ln(p/m)

     which is a straight line in log commits through the two anchors, and
     the IDENTITY when m = m0 and p = p0 — his own record is the one case
     where the rule does nothing, which is what makes his sheet 0 px and
     what makes this a scale and not a filter. Nothing else in the engine
     knows: the height of a day has always been sqrt(commits), so c^ goes
     in there and the contours, the tints, the hachures, the sea, the
     beyond and the section all read the one scale without a uniform.

     WHAT THE READER GETS. Sea level is five sheet-commits and stays so,
     but on a record with a quieter ordinary day it now falls at a
     SMALLER REAL NUMBER — that is the whole fault Tommy named: a handle
     whose median is under five was all water. Richard Watkins's sea
     falls at 5.4 of his commits, Linus Torvalds's at 2.9, and a handle
     with a median of 3 has its quiet days on dry land for the first
     time. At the top his best day now reaches the tone and the height
     Tommy's best day reaches instead of dying in the second ochre.

     EVERY PRINTED NUMBER IS STILL THE RECORD'S OWN. `from()` is the way
     back, and the key's tint figures, the section's levels and the
     contour figures all come back through it, so what is inked on a
     stranger's sheet is a number of HIS commits, never a number of
     Tommy's.

     THE FLOOR. A short record cannot be levelled, and it is the PEAK and
     not the median that fails first: measured over consecutive windows
     of the three long records in the cache (REPORT-room-R70.md carries
     the table), a 30-day window of Tommy's own record gives B anywhere
     from 1.5 to 4.0 where the whole record gives 1.0, because the best
     day of a short record has not happened yet. The median settles by
     about thirty levelled days (Richard's wanders 8..15 under thirty and
     9..14 over it); the peak never settles, it only grows. So thirty
     levelled working days is the floor, and under it the record is drawn
     on the PRINTED ladder with no stretch at all — the honest answer
     being that there is not yet a record to cut a scale from.

     THE CAP. B runs away when a record has no spread (p = m gives an
     infinite power, and a flat record would have its noise turned into
     mountains). The stretch stops where the record's own quiet days
     would start to drown: at most HALF AN ORDINARY DAY may go under
     water, i.e. m0 * 0.5^B >= 5, so B <= log2(m0/5) = 2.137 on the filed
     record. Richard sits at 2.063 — just inside it, which is worth
     knowing. A flat record is capped there and comes out as what it is:
     low downs, no summit. */
  var LEVEL_FLOOR = 30;                 /* levelled working days */
  var CAP_SHARE = 0.5;                  /* half an ordinary day stays land */
  function cutLadder(lev, home) {
    if (!home || !(home.m > 0) || !(home.p > home.m)) return null;   /* his own sheet */
    var S = { n: lev.n, m: lev.m, p: lev.p, m0: home.m, p0: home.p,
              beta: 1, cap: 0, capped: false, floored: false, sea: C.BASELINE_COMMITS };
    S.cap = Math.log(home.m / C.BASELINE_COMMITS) / Math.log(1 / CAP_SHARE);
    if (!(lev.n >= LEVEL_FLOOR) || !(lev.m > 0)) { S.floored = true; return S; }
    var b = Math.log(home.p / home.m) / Math.log(lev.p / lev.m);     /* Infinity when p === m */
    if (!(b > 0)) b = S.cap;
    if (b > S.cap) { b = S.cap; S.capped = true; }
    S.beta = b;
    S.sea = ladder(S).from(C.BASELINE_COMMITS);
    return S;
  }
  /* the two directions, built from the plain numbers so that the worker,
     the main thread and the furniture all use the one arithmetic. The
     identity is taken BY TEST and not by pow(x,1): a record that is the
     filed record does not go near a logarithm. */
  function ladder(S) {
    var id = !S || S.floored || (S.beta === 1 && S.m === S.m0);
    if (id) return { id: true, to: function (c) { return c; }, from: function (v) { return v; } };
    var m = S.m, m0 = S.m0, b = S.beta, ib = 1 / S.beta;
    return { id: false,
             to: function (c) { return c > 0 ? m0 * Math.pow(c / m, b) : 0; },
             from: function (v) { return v > 0 ? m * Math.pow(v / m0, ib) : 0; } };
  }

  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function isoOf(d) { return d.toISOString().slice(0, 10); }
  function dateOf(iso) { return new Date(iso + 'T00:00:00Z'); }
  function dowOfRow(r) { return C.WEEK_START === 'sunday' ? (r + 6) % 7 : r; }
  function isWorkRow(r) { return dowOfRow(r) < 5; }
  function smoothstep(a, b, v) { var t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }

  /* ---- field maths (survey-map.js :1075 onwards) ----------------- */
  function buildField(grid, nx, ny, ux, uy, padX, padY) {
    if (padY === undefined) padY = padX;
    var W = nx * ux + padX * 2, H = ny * uy + padY * 2;
    var f = new Float32Array(W * H);
    for (var py = 0; py < ny * uy; py++) {
      var gy = (py + 0.5) / uy - 0.5, y0 = Math.floor(gy), ty = gy - y0;
      var ya = clamp(y0, 0, ny - 1), yb = clamp(y0 + 1, 0, ny - 1);
      for (var px = 0; px < nx * ux; px++) {
        var gx = (px + 0.5) / ux - 0.5, x0 = Math.floor(gx), tx = gx - x0;
        var xa = clamp(x0, 0, nx - 1), xb = clamp(x0 + 1, 0, nx - 1);
        f[(py + padY) * W + (px + padX)] =
          grid[ya * nx + xa] * (1 - tx) * (1 - ty) + grid[ya * nx + xb] * tx * (1 - ty) +
          grid[yb * nx + xa] * (1 - tx) * ty + grid[yb * nx + xb] * tx * ty;
      }
    }
    return { data: f, W: W, H: H, padX: padX, padY: padY, ux: ux, uy: uy };
  }
  function cloneField(f) {
    return { data: new Float32Array(f.data), W: f.W, H: f.H, padX: f.padX, padY: f.padY, ux: f.ux, uy: f.uy };
  }
  function extendEdges(f, ny) {          // :1108
    var W = f.W, pad = f.padY, uy = f.uy;
    var top = pad, bot = pad + ny * uy - 1, hold = pad >> 1;
    for (var y = hold; y < pad; y++) f.data.copyWithin(y * W, top * W, top * W + W);
    for (var y2 = bot + 1; y2 < f.H - hold; y2++) f.data.copyWithin(y2 * W, bot * W, bot * W + W);
    return f;
  }
  function boxBlurH(field, r) {
    var W = field.W, H = field.H, d = field.data, row = new Float32Array(W);
    for (var y = 0; y < H; y++) {
      var o = y * W;
      for (var i = 0; i < W; i++) row[i] = d[o + i];
      var sum = 0;
      for (var k = -r; k <= r; k++) sum += row[clamp(k, 0, W - 1)];
      var inv = 1 / (2 * r + 1);
      for (var x = 0; x < W; x++) {
        d[o + x] = sum * inv;
        sum += row[clamp(x + r + 1, 0, W - 1)] - row[clamp(x - r, 0, W - 1)];
      }
    }
  }
  function boxBlurV(field, r) {
    var W = field.W, H = field.H, d = field.data, col = new Float32Array(H);
    for (var x = 0; x < W; x++) {
      for (var i = 0; i < H; i++) col[i] = d[i * W + x];
      var sum = 0;
      for (var k = -r; k <= r; k++) sum += col[clamp(k, 0, H - 1)];
      var inv = 1 / (2 * r + 1);
      for (var y = 0; y < H; y++) {
        d[y * W + x] = sum * inv;
        sum += col[clamp(y + r + 1, 0, H - 1)] - col[clamp(y - r, 0, H - 1)];
      }
    }
  }
  function smooth(field, sigmaX, sigmaY) {   // :1130
    var rx = Math.max(1, Math.round(sigmaX * 0.85));
    var ry = Math.max(1, Math.round((sigmaY === undefined ? sigmaX : sigmaY) * 0.85));
    for (var pass = 0; pass < 3; pass++) { boxBlurH(field, rx); boxBlurV(field, ry); }
    return field;
  }
  function f0W(f) { return f.W; }
  /* ==== JG1: THE GROUND'S HEIGHT, IN JS — engine.js FIELD_GLSL fieldAt:
     Catmull-Rom over the finished field (`f.bed` on its lattice `f.lat`,
     margin in whole cells), indices clamped to the array as the texture's
     CLAMP_TO_EDGE clamps them. The shader folds the two middle taps of
     each axis into one bilinear fetch, which is the same sum. It replaced a bilinear `sampleAt` nobody called;
     day.js bedAt (and through it Room.bedAt, the voyage's keel and the
     pick) reads this, so what stands on the ground stands on the surface
     the mesh is displaced to. ==== */
  var CRW = [0, 0, 0, 0], CRV = [0, 0, 0, 0];
  function crw(t, w) {
    var t2 = t * t, t3 = t2 * t;
    w[0] = -0.5 * t3 + t2 - 0.5 * t; w[1] = 1.5 * t3 - 2.5 * t2 + 1;
    w[2] = -1.5 * t3 + 2 * t2 + 0.5 * t; w[3] = 0.5 * t3 - 0.5 * t2;
  }
  function fieldAtJS(f, mx, my) {
    var lat = f.lat, W = f.W, H = f.H, d = f.bed;
    var tx = (mx - lat.X0) / lat.DX + (lat.MX || 0), ty = (my - lat.Y0) / lat.DY + (lat.MY || 0);
    var x0 = Math.floor(tx), y0 = Math.floor(ty), acc = 0, i, j;
    crw(tx - x0, CRW); crw(ty - y0, CRV);
    for (j = 0; j < 4; j++) {
      var row = Math.min(Math.max(y0 + j - 1, 0), H - 1) * W, r = 0;
      for (i = 0; i < 4; i++) r += CRW[i] * d[row + Math.min(Math.max(x0 + i - 1, 0), W - 1)];
      acc += CRV[j] * r;
    }
    return acc;
  }
  /* A LEVEL, in the units the ground is plotted in (:1422). */
  function datumLevel(commits, ux, uy, padX, padY, sigmaX, sigmaY) {
    var W = 12, g = new Float32Array(W * 7);
    for (var wi = 0; wi < W; wi++)
      for (var di = 0; di < 7; di++) {
        var row = (C.BOUSTROPHEDON && (wi & 1)) ? 6 - di : di;
        g[row * W + wi] = isWorkRow(di) ? Math.sqrt(commits) : 0;
      }
    var f = smooth(extendEdges(buildField(g, W, 7, ux, uy, padX, padY), 7), sigmaX, sigmaY);
    var sum = 0, n = 0, fx = padX + Math.round(6 * ux);
    for (var d = 0; d < 7; d++) {
      if (!isWorkRow(d)) continue;
      sum += f.data[(padY + Math.round((d + 0.5) * uy)) * f.W + fx]; n++;
    }
    return sum / n;
  }


  /* =====================================================================
     R35: ONE GROUND — THE FIELD CONTINUES PAST THE NEAT LINE.

     Tommy, 2026-09-14: "this 'closing the gap' between the edging and the
     map doesn't seem to be working, we get closer but it doesn't seem to be
     able to be pixel perfect? is it a genuinely really tough problem?" and
     "we just seem to be approximating the curve with straight lines and i
     don't know why?".

     It was a tough problem because it was the WRONG problem. Six items
     (R3, R10, R16, R19, R20, R28) each closed a symptom at the neat line
     and none could close the line, because there were TWO OBJECTS: the
     record's ground on the field's own lattice, and a second drawing (the
     beyond) on its own view-following grid, with its own program and its
     own material, built by one rule — every point outside the line reads
     the record's boundary point NEAREST it. That rule EXTRUDES: every
     value walks straight out along the normal, so a curve at the edge
     becomes a straight band, and between the two meshes a curtain had to
     hide a height mismatch that can only ever be biased, never made exact.

     There is one object now, and it starts here. The field carries MARGIN
     rows and columns to the north, the south and the west, and they are
     the record's own field CONTINUED — not mirrored, not extruded:

     - the continuation is a DIFFUSION. Walking one row out at a time, the
       row is the previous row blurred ALONG the line, by whatever radius
       brings the accumulated spread to `BLUR x the distance out` (R20's
       own constant, 0.35, now a property of the ground rather than of a
       shader). Iterating a blur is the heat kernel, so a bump at the line
       opens into a lobe, two headlands merge, a narrow inlet closes over —
       and a contour LEAVES the line and bends on, because the value at a
       point out here is an average over a widening piece of the line and
       not the one point opposite it.
     - the first `SEED` rows are left exactly as the record's own smoothing
       left them. `smooth()`'s kernel already crosses the line (it is run
       over the padded field with the edge row held), so the near margin is
       already the record's own arithmetic, to the bit — which is what
       keeps the fit diff 0 when a fragment at the neat line takes its
       central difference one cell outside.
     - then it is EASED to the paper datum over R1's two runs, the same two
       constants in the same order the conjecture used them: FLAT_RUN down
       to FLATTEN of itself, then the SINK to nothing. At the line the ease
       is exactly 1, so the margin IS the record there, to the float.
     - and SEA ROWS STAY SEA (R31). The target of the ease is the datum on
       land and the record's own western DEPTH at sea, held wherever the
       ACROSS overshoot is zero — so west of January 2022 the Precambrian
       Sea goes on at its own depth without end (Tommy: "the sea doesn't
       have the depth of the precambrian sea before it, can we make it an
       identical extension of that?"), and north and south it sinks with
       the rows that have genuinely stopped.

     Past the margin the texture is CLAMP_TO_EDGE, and the margin's last
     row is already the target — flat paper at the datum north and south,
     the sea's own floor west. So the surface goes on for ever for nothing.
     ===================================================================== */
  var BEY = {
    FLAT_RUN: 0.34,      // water.js FLAT_RUN (relief.js :246 eased, R1 a)
    FLATTEN: 0.34,       // water.js BEYOND_FLATTEN (relief.js :246)
    SINK: 1 + 1 / 6,     // water.js BEYOND_SINK
    BLUR: 0.35,          // water.js BEY_BLUR (R20 d), now the diffusion's rate
    OPEN_SEA: 0.75,      // water.js's openSea run (R3 c)
    SEED: 8,             // rows/columns of the record's own smoothing, kept
    /* R35 (the front): the run the ground falls over at the survey front,
       in MAP units. Tommy, 2026-09-14: "i'm open on how the cliff should
       work ... it does have to drop from the top to terra incognita, it
       could be a similar edge to the sides or even slope down if we
       wanted?" — so the front is the SAME construction as the three other
       sides, the field eased to the datum, only over a SHORT run: about
       three field cells and one or two of the mesh's, which is a scarp of
       roughly seven to one at the 2026 front and reads as a cut rather
       than a hill. It is NOT a vertical wall, so there is no doubled
       vertex column, no second mesh and no second program anywhere, and
       R28 OPEN 2 (the plunge resolved as a chord by the ground's own
       pitch, hidden behind a face standing 0.35 proud of it) has nothing
       left to be. */
    SCARP: 14
  };

  /* =====================================================================
     R40: AND TERRA INCOGNITA STANDS ON GROUND.

     Tommy, 2026-09-15: "we had an issue with the terrain in [Terra
     Incognita] because we plunged down that big scarp cliff. Rather than
     landing at, say, sea level, we land right at the ground ... surely, you
     just carry on the model into the terra incognita. You drop it down, and
     the model carries on to the east indefinitely as a solid object, and
     you can't see through it or have any weirdness."

     Until this item the east of the cut was a DEAD PLANE: (3) (i) eased the
     bed to exactly 0, which is the COAST, a hair (0.02 bed, 0.4 sheet) over
     the water plane. So the scarp's foot met a sheet of paper lying flat on
     the water, and every join down there — foot, plane, the plate's own cut
     — was three surfaces within half a sheet unit of each other.

     The east is LOW DOWNS now: unsurveyed country, gentle, well clear of
     the water, going on east without end.

     AND THAT IS WHY IT IS NOT IN THE ARRAY. The obvious build is to write
     the relief into b2 east of the cut, but the field is a TEXTURE and
     the texture is CLAMP_TO_EDGE: past the lattice's last column (x =
     2,919.5 against a terra view that already reaches 2,830) every row
     would hold its last value for ever and the downs would come out as
     east-west ridges running to the horizon. Growing the margin east only
     moves that line — the cap is 8,192 cells and the pan has no cap. So
     the downs are an ANALYTIC field, evaluated in the shader, exact at
     every distance and costing the array nothing (no new texels, no new
     bytes, no new margin: the east margin stays 0).

     The array still does the falling. (3) (i) below eases the record's own
     bed to 0 over SCARP, and the shader FADES THE DOWNS IN over the SAME
     run (terraW), so the sum is a crossfade from the top of the record to
     the downs: the foot of the scarp lands on ground, and the fall's
     length is still SCARP. Neither side has a step in it.

     THE HASH IS TAKEN AGAINST THE LATTICE ORIGIN, the way the hachure's
     scatter is (see continueField's closing note): the origin never moves,
     so the downs are dealt once and no pan, zoom or margin re-deals them.

     THE NUMBERS ARE IN CONTOUR INTERVALS — the plate's own, and the one
     the eye actually has at the fit: the INDEX generation, 8 x TINT_STEP
     = sqrt(5) = 2.236068 bed units, 44.72 sheet units under the twentyfold
     exaggeration (engine.js's uStep ladder draws the intermediate at a
     half of it and the finest at a quarter, and both of those only fade in
     with the zoom). The downs' mean sits MEAN_STEPS of it above the coast
     and they swing AMP_STEPS either side, so the lowest ground they can
     reach is (MEAN_STEPS - AMP_STEPS) intervals over the coast and the
     water plane (-0.02 bed) cannot show through them anywhere.

     MEAN is held at one and a half times AMP in both settings below, so
     halving the amplitude does not also drop the downs onto the water.
     ===================================================================== */
  var TERRA = {
    AMP_STEPS: 0.5,     // the swing either side of the mean, in intervals
    MEAN_STEPS: 0.75,   // how far over the coast the downs sit, in intervals
    /* the lobes, in plate-heights: one broad and one half as wide at half
       the weight, which is the least that stops a single-octave field
       reading as a regular quilt */
    LOBE1: 0.85, LOBE2: 0.37,
    W1: 0.66, W2: 0.34
  };

  /* the GLSL the ground is drawn with. uTerra is (amp, mean, 1/lobe1,
     1/lobe2) in bed and map units; amp 0 means NO DOWNS, so any program
     that never receives it draws the dead plane it always drew.

     hash21 is Dave Hoskins' sine-free hash: multiply, fract, dot, add and
     nothing else, so it is the same number on every conforming GPU (a
     sin()-based hash is not, and the downs have to be the same downs on
     Tommy's screen and in the harness). vnoise returns the value AND its
     two derivatives, because the hill shading needs the slope and four
     extra evaluations of a two-octave field per fragment is a price the
     east should not pay. The interpolant is QUINTIC (C2): with the cubic
     one the second derivative steps at every cell boundary and a raking
     sun draws that step as a faint quilt. */
  /* ==== R46: THE NOISE IS ITS OWN CHUNK NOW. R40 wrote the hash and the
     quintic value noise inside TERRA_GLSL because one drawing wanted them;
     the open sea's floor (SEABED_GLSL below) wants exactly the same two,
     and it is drawn by the WATER program as well as the ground's, which
     does not include TERRA. So the two functions are lifted out, byte for
     byte, and TERRA_GLSL is NOISE_GLSL + what is left of it — every
     program that included TERRA_GLSL gets the identical source it got
     before, in the same order. ==== */
  var NOISE_GLSL = [
    'float tHash21(vec2 p){',
    '  vec3 q = fract(vec3(p.x, p.y, p.x) * vec3(0.1031, 0.1030, 0.0973));',
    '  q += dot(q, q.yzx + 33.33);',
    '  return fract((q.x + q.y) * q.z);',
    '}',
    '/* (value, d/dpx, d/dpy) of a quintic value noise on the unit lattice */',
    'vec3 tVnoise(vec2 p){',
    '  vec2 i = floor(p), f = p - i;',
    '  vec2 u = f*f*f*(f*(f*6.0 - 15.0) + 10.0);',
    '  vec2 du = 30.0*f*f*(f*(f - 2.0) + 1.0);',
    '  float a = tHash21(i), b = tHash21(i + vec2(1.0,0.0));',
    '  float c = tHash21(i + vec2(0.0,1.0)), d = tHash21(i + vec2(1.0,1.0));',
    '  float k1 = b - a, k2 = c - a, k3 = a - b - c + d;',
    '  return vec3(a + k1*u.x + k2*u.y + k3*u.x*u.y,',
    '              du.x*(k1 + k3*u.y),',
    '              du.y*(k2 + k3*u.x));',
    '}',
    ''
  ].join('\n');

  /* =====================================================================
     D1 — THE PIT IN THE CONJECTURE, AND THE GROUND THE PARTY HAS PACED

     Two terms, both of them GROUND, both of them 0 to the bit until the
     engine hands over a uniform — so every program that includes this
     chunk draws exactly what it drew before the item landed.

     doomAt   a smooth radial bowl in Terra Incognita, centred where the
              survey's conjecture runs out. uDoom is (centre x, centre y,
              radius, signed rise in bed units): NEGATIVE is the pit and
              POSITIVE the peak, and the engine eases the fourth term from
              one to the other, so the hole fills, flattens and stands up
              through ONE number and there is no second field to cross to.
              The profile is (1 - t^2)^3, which is 1 at the centre and 0
              with zero slope at the rim, so the downs close over it
              without a seam; the value AND the two derivatives come back
              because the east is shaded off the ground's own slope and a
              hole that takes no light is not a hole.

     paceVec  the seven days of the front, as the party walks them. Each
              cell the surveyor stands on is LEVELLED — the ground under
              it rises to a surveyed height — and that is a pad of the
              cell's own size added to the record's field. The edge run
              straddles the cell's line (half in, half out) because
              smoothstep is symmetric about its middle: two paced cells
              side by side sum to exactly 1 across the line they share,
              so a pair of levelled days is one table and not two hummocks
              with a ditch between them.
     ===================================================================== */
  /* ==== D2 (1): THE FUNNEL'S OWN TWO NUMBERS. Tommy, 2026-09-19: "it's
     an indentation versus a hill currently. What I really wanted is for
     it to go off to absurdity — imagine more of a black hole and how
     that's classically represented visually."

     So the profile is not a bowl any more. It is a 1/r THROAT: g(t) =
     1/(t+K) - 1/(1+K), which is the classic gravity well's own curve —
     shallow at the rim, and falling away faster and faster as it goes in,
     with no bottom of its own. DEEP is what stops it: inside t = DEEP the
     value is held at its own floor, so the throat ends in a small flat
     disc the mesh can actually carry instead of a singular spike that no
     vertex density resolves (and the funnel's floor is the darkest thing
     on the sheet, which is where the picture wants it anyway).

     THE RIM IS STILL C1. g is multiplied by the window (1 - t*t), which
     is 0 at the rim AND kills the slope there (g(1) = 0, so the product
     rule leaves nothing behind), exactly as the old (1-t^2)^3 bowl did —
     the downs close over the funnel's edge without a seam.

     NRM makes the profile exactly 1 at the floor, so uDoom.w is the
     funnel's DEPTH in bed units and the engine still eases one number
     from the pit to the peak. ==== */
  var FUN = { K: 0.16, DEEP: 0.06 };
  FUN.NRM = 1 / ((1/(FUN.DEEP + FUN.K) - 1/(1 + FUN.K)) * (1 - FUN.DEEP*FUN.DEEP));

  var TERRA_GLSL = NOISE_GLSL + [
    'uniform vec4 uTerra;   /* R40: amp, mean, 1/lobe1, 1/lobe2 */',
    'uniform vec4 uDoom;    /* D1: centre x, centre y, radius, signed rise */',
    '/* D2 (1): the funnel, its value and its two map derivatives */',
    'float doomF(float t){',
    '  float tt = max(t, ' + FUN.DEEP + ');',
    '  return (1.0/(tt + ' + FUN.K + ') - 1.0/(1.0 + ' + FUN.K + '))',
    '         *(1.0 - t*t)*' + FUN.NRM + ';',
    '}',
    'vec3 doomAt(vec2 s){',
    '  if (uDoom.z <= 0.0 || abs(uDoom.w) < 1e-6) return vec3(0.0);',
    '  vec2 d = s - uDoom.xy;',
    '  float r = length(d);',
    '  if (r >= uDoom.z) return vec3(0.0);',
    '  float t = r / uDoom.z, tt = max(t, ' + FUN.DEEP + ');',
    '  float gg = 1.0/(tt + ' + FUN.K + ') - 1.0/(1.0 + ' + FUN.K + ');',
    '  float win = 1.0 - t*t;',
    '  float f = gg*win*' + FUN.NRM + ';',
    '  float dg = t > ' + FUN.DEEP + ' ? -1.0/((tt + ' + FUN.K + ')*(tt + ' + FUN.K + ')) : 0.0;',
    '  float dfdr = (dg*win - 2.0*t*gg)*' + FUN.NRM + ' / uDoom.z;',
    '  vec2 g = r > 1e-4 ? (d / r) * dfdr : vec2(0.0);',
    '  return vec3(uDoom.w*f, uDoom.w*g.x, uDoom.w*g.y);',
    '}',
    'uniform vec4 uPace[7]; /* D1: x, y (map), the rise (bed), unused */',
    'uniform vec2 uPaceK;   /* D1: the cell\'s half width and half height */',
    '/* a pad edge: 1 within a, 0 beyond b, with its own slope */',
    'vec2 padE(float d, float a, float b){',
    '  if (d <= a) return vec2(1.0, 0.0);',
    '  if (d >= b) return vec2(0.0, 0.0);',
    '  float t = (d - a)/(b - a);',
    '  return vec2(1.0 - t*t*(3.0 - 2.0*t), -6.0*t*(1.0 - t)/(b - a));',
    '}',
    'vec3 paceVec(vec2 s){',
    '  vec3 a = vec3(0.0);',
    '  if (uPaceK.x <= 0.0) return a;',
    '  float rx = 0.11*uPaceK.x, ry = 0.11*uPaceK.y;',
    '  for (int i = 0; i < 7 + uZero; i++){',
    '    float h = uPace[i].z;',
    '    if (h <= 0.0) continue;',
    '    float dx = s.x - uPace[i].x, dy = s.y - uPace[i].y;',
    '    vec2 ex = padE(abs(dx), uPaceK.x - rx, uPaceK.x + rx);',
    '    if (ex.x <= 0.0) continue;',
    '    vec2 ey = padE(abs(dy), uPaceK.y - ry, uPaceK.y + ry);',
    '    if (ey.x <= 0.0) continue;',
    '    a += vec3(h*ex.x*ey.x, h*ex.y*sign(dx)*ey.x, h*ey.y*sign(dy)*ex.x);',
    '  }',
    '  return a;',
    '}',
    '/* ==== D2 (3): AND A DAY THE PARTY HAS PACED IS SURVEYED GROUND.',
    '   Tommy, 2026-09-19: "our step into the unknown should redraw that',
    '   from being grey to being coloured, raised-up ground in the same',
    '   way ... by walking any unpaved cell, we are having it surveyed."',
    '   paceVec raises the GROUND; this is the same pad read as the',
    '   record\'s own SURVEYEDNESS, so the engine can hand it to the',
    '   material as surveyedAt\'s equal and the cell takes the record\'s',
    '   colour ramp at its paced height instead of Terra Incognita\'s',
    '   grey. Max and not sum: two pads that overlap are one surveyed',
    '   patch, never a doubly-surveyed one.',
    '   BG1 (2026-09-26): AND THE WALLS ARE SURVEYED TOO. Tommy: the paced',
    '   block "raises up ... still has the unsurveyed colour underneath".',
    '   The mask used to share paceVec\'s own edge, so it was 0.5 halfway',
    '   up the wall and 0 at its foot: the wall was half Terra Incognita\'s',
    '   paper. It is 1 now over the whole pad AND its wall (to the foot at',
    '   k + r) and fades only on the flat beyond the foot, over half a wall. */',
    'float paceMask(vec2 s){',
    '  float a = 0.0;',
    '  if (uPaceK.x <= 0.0) return a;',
    '  float rx = 0.11*uPaceK.x, ry = 0.11*uPaceK.y;',
    '  for (int i = 0; i < 7 + uZero; i++){',
    '    if (uPace[i].z <= 0.0) continue;',
    '    float ex = padE(abs(s.x - uPace[i].x), uPaceK.x + rx, uPaceK.x + 1.5*rx).x;',
    '    if (ex <= 0.0) continue;',
    '    float ey = padE(abs(s.y - uPace[i].y), uPaceK.y + ry, uPaceK.y + 1.5*ry).x;',
    '    a = max(a, ex*ey);',
    '  }',
    '  return a;',
    '}',
    'float terraW(float sx){',
    '  if (uTerra.x <= 0.0 || uScarp.y <= 0.0) return 0.0;',
    '  return smst(uScarp.x, uScarp.x + uScarp.y, sx);',
    '}',
    '/* the downs, in bed units, with d(bed)/dx and d(bed)/dy per map unit */',
    'vec3 downsAt(vec2 s){',
    '  vec2 o = s - uLat.xy;       /* the hash against the lattice origin */',
    '  vec3 n1 = tVnoise(o*uTerra.z), n2 = tVnoise(o*uTerra.w + 19.7);',
    '  float n  = ' + TERRA.W1 + '*n1.x + ' + TERRA.W2 + '*n2.x;',
    '  float gx = ' + TERRA.W1 + '*n1.y*uTerra.z + ' + TERRA.W2 + '*n2.y*uTerra.w;',
    '  float gy = ' + TERRA.W1 + '*n1.z*uTerra.z + ' + TERRA.W2 + '*n2.z*uTerra.w;',
    '  vec3 dm = doomAt(s);   /* D1: and the pit (or the peak) in it */',
    '  return vec3(uTerra.y + uTerra.x*(2.0*n - 1.0) + dm.x,',
    '              2.0*uTerra.x*gx + dm.y, 2.0*uTerra.x*gy + dm.z);',
    '}',
    '/* what the FIELD does not carry: 0 on the record and west of the cut, */',
    '/* to the bit, and the downs east of the scarp\'s foot. */',
    '/* D1: + the levelled ground of the days the party has paced, which',
    '   is on the RECORD and so is added outside the front\'s own gate. */',
    '/* ==== LP2: AND IT IS READ, NOT COMPUTED, WHERE THE BAKE IS THERE.',
    '   The whole of it — terraW, downsAt (two octaves of quintic value',
    '   noise plus the funnel) and the seven-pad paceVec — is a function of',
    '   the map point and of uniforms that only change when the record, the',
    '   funnel or a paced day does. engine.js bakes it into uBake.r once per',
    '   change; here every consumer takes ONE texture read instead of a copy',
    '   of the chain inlined at each of its call sites (the lettering has',
    '   eleven). The analytic body below is what the bake program itself is',
    '   compiled with, and what a context without EXT_color_buffer_float',
    '   still draws. ==== */',
    '#ifdef BAKED',
    '/* PF2 (2026-09-25): + the funnel, analytic. The bake covers the FIELD\'s',
    '   rectangle and the pit stands on mid-2030, four plate-widths east of',
    '   it, where the clamped read is the edge\'s value: since LP2 the pit',
    '   (and the peak) moved no ground at all, only the shading, which reads',
    '   downsAt itself (Tommy: "the pit is now always 2D and flat rather than',
    '   a big pit that can be inverted"). So the bake is drawn with the funnel',
    '   off (engine.js ensureBake) and every consumer adds it here — one',
    '   length and an early out beyond the radius, not the noise chain. */',
    'float terraOf(vec2 s){ return texture(uBake, uvOf(s)).r + terraW(s.x)*doomAt(s).x; }',
    '#else',
    'float terraOf(vec2 s){ float w = terraW(s.x);',
    '  return (w <= 0.0 ? 0.0 : w*downsAt(s).x) + paceVec(s).x; }',
    '#endif',
    ''
  ].join('\n');

  /* the same field in JS, for the report's numbers and for anything on the
     main thread that has to know how high the downs stand. float32 at every
     step, so it is the shader's arithmetic and not a double's. */
  var f32 = Math.fround;
  function fr(v){ return f32(v - Math.floor(v)); }
  function hash21(px, py){
    var qx = fr(f32(px*f32(0.1031))), qy = fr(f32(py*f32(0.1030))), qz = fr(f32(px*f32(0.0973)));
    var K = f32(33.33);
    var d = f32(f32(f32(qx*f32(qy + K)) + f32(qy*f32(qz + K))) + f32(qz*f32(qx + K)));
    qx = f32(qx + d); qy = f32(qy + d); qz = f32(qz + d);
    return fr(f32(f32(qx + qy)*qz));
  }
  function vnoiseJS(px, py){
    var ix = Math.floor(px), iy = Math.floor(py);
    var fx = f32(px - ix), fy = f32(py - iy);
    var ux = f32(fx*fx*fx*f32(f32(fx*f32(fx*6 - 15)) + 10));
    var uy = f32(fy*fy*fy*f32(f32(fy*f32(fy*6 - 15)) + 10));
    var a = hash21(ix, iy), b = hash21(ix + 1, iy);
    var c = hash21(ix, iy + 1), d = hash21(ix + 1, iy + 1);
    var k1 = f32(b - a), k2 = f32(c - a), k3 = f32(f32(f32(a - b) - c) + d);
    return f32(f32(f32(a + f32(k1*ux)) + f32(k2*uy)) + f32(f32(k3*ux)*uy));
  }
  /* s in map units, o the lattice origin, PA the plate-height, amp/mean in
     BED units */
  function downsJS(sx, sy, X0, Y0, PA, amp, mean){
    var ox = f32(sx - X0), oy = f32(sy - Y0);
    var i1 = f32(1/(TERRA.LOBE1*PA)), i2 = f32(1/(TERRA.LOBE2*PA));
    var n1 = vnoiseJS(f32(ox*i1), f32(oy*i1));
    var n2 = vnoiseJS(f32(f32(ox*i2) + 19.7), f32(f32(oy*i2) + 19.7));
    var n = f32(f32(TERRA.W1*n1) + f32(TERRA.W2*n2));
    return f32(mean + f32(amp*f32(2*n - 1)));
  }

  /* =====================================================================
     R46 — THE ZERO-COMMIT SEA IS ALIVE   (`?west=`)

     Tommy, 2026-09-14: "it'd be good to have something like a swell
     pattern? any thoughts about adding some actual terrain/depth, maybe
     random/generative to make it seem more real/alive and not just blue?"

     R39 gave the west its honest floor: a zero-commit month of 2022 has
     bed = -COAST exactly, so west of the surveyed start the sea is at the
     floor at every row, for ever. That is TRUE and it is also ONE TONE —
     R39 OPEN 2, R41 OPEN 1 and X5 OPEN 5 are all the same sentence, and
     X5's is the sharpest: before routine figures were removed, the lead
     read 21 fathoms at every cast from 2022 to 1986 and made a column of
     identical numbers.

     Nothing can be DATA out there. So what is offered is two stated
     CONVENTIONS, each on its own switch, and neither is the default:

       ?west=swell   the sheet's own hand — the engraver's water-lining,
                     fine crests parallel to the western shore, crowding
                     at the coast and opening out to sea, drawn as INK on
                     the water plane with no height at all (water.js).
       ?west=bed     a generative SEABED — this file's fbm, added to the
                     bed west of the line, so the depth tints, the eight
                     waterlines and `Room.bedAt` all find shoal banks and
                     deeps where today they find one number.
       ?west=both    both.
       (absent, or ?west=0)  today's flat sea, to the bit.

     WHY IT IS ANALYTIC AND NOT IN THE ARRAY. Exactly R40's reason, and
     more so: the field's lattice stops 130 map units west of the record
     (R39 §2) and the voyage's 1986 is 74,000 units further west. Putting
     this in the texture means about fifteen thousand new columns of it.
     So it is evaluated in the shader, mirrored here in JS for `bedAt`,
     and costs the array nothing: no new texels, no new bytes, no wider
     margin.

     THE SAME SEA EVERY VISIT. The hash is taken against the LATTICE
     ORIGIN, as R40's downs are — the origin never moves (continueField's
     closing note), so the banks are dealt once and no pan, zoom, margin
     or reload re-deals them.

     THE NUMBERS, AND WHY THEY CANNOT DRY. The bed carries three tones
     (engine.js :825-827): SEA at the coast, SEA1 below -0.47979658 and
     SEA2 below -1.17666850; the floor is -2.128613305. AMP and MEAN are
     NOT guesses — the three-octave sum was measured over 82,650 points of
     open sea west of -8,700 and its normalised value u = 2n-1 came out
       min -0.888   p05 -0.491   p50 -0.0005   p95 +0.414   max +0.743
     (a sum of three value noises is not uniform; it piles up in the
     middle, which is why the extremes of the formula are never seen and
     sizing to them would have made a dead flat sea). The two numbers are
     then solved from the two tint lines that matter:
       the SHALLOWEST ground anywhere is floor + MEAN + AMP x 0.743
         = -0.622 bed, which is 0.14 BELOW the SEA1 line — so the open sea
         never shoals into the first tint band and can nowhere come out of
         the water (the plane's own wet test is bed < -0.01);
       the top DECILE of it stands over the SEA2 line (u > 0.317), so
         about a tenth of the sea is drawn as bank in the middle tint and
         the rest is deep;
       the DEEPEST is floor + MEAN - AMP x 0.888 = -2.743.
     In the voyage's fathoms (x 10) that is a floor between 6.2 and 27.4,
     median 15.9, against today's flat 21.3 — a lead line with something
     to say, and a leadsman who can call "she is in shoal water" and mean
     it. Her grounding rule is untouched (bed above -0.3): the seabed
     alone can never trip it, because its shallowest point is 0.32 below
     that line, and the banks and the named shoals are still added ON TOP
     of it in voyage.js, never under it.
     ===================================================================== */
  var SEABED = {
    AMP: 1.30,        // the swing either side of the mean, in BED units
    MEAN: 0.54,       // the mean floor, over -COAST, in BED units
    /* the lobes, in PLATE-HEIGHTS (546 map units here): a bank about five
       hundred units across, a second at a third of that, and a third for
       the small ground the lead would actually find */
    LOBE1: 0.95, LOBE2: 0.42, LOBE3: 0.18,
    W1: 0.55, W2: 0.30, W3: 0.15,
    /* ---- the SWELL, drawn by water.js on the plane ----------------
       The first cut of these (SP0 11, OPEN 1200, meanders 620 and 255
       units long) came out as FABRIC — a regular wavy grating, too curly
       and too even to read as a drawn line (R46-his-swell-A.png). The
       crests never cross whatever the meander does, because the meander
       shifts every crest at a given y by the same amount; what was wrong
       was the RATE. So: fewer lines, opening out faster, and a swell that
       runs a plate-height and a half before it repeats. */
    SP0: 14,          // the crest spacing at the western shore, map units
    OPEN: 900,        // the run over which that spacing doubles
    /* AND THEN IT STOPS OPENING. "Opens out to sea" taken literally over
       an ocean 74,000 units wide is not a convention, it is a few stray
       curves: at 2015 the spacing had reached 241 map units and the flat
       fit carried EIGHT crests across the whole plate
       (R46-fit2015-swell-A.png). So the spacing opens to CAP times the
       shore's and then holds, which is what an engraver does — he crowds
       the lines at the coast, opens them a little, and then rules the
       ocean evenly. C1 at the join: the log and the straight line meet at
       the same value AND the same slope, so no crest kinks there. */
    CAP: 2.2,
    A1: 30, L1: 1400, // the meander: two sines in y, map units
    A2: 9,  L2: 560,
    WID: 0.62,        // the crest's width, device px at ratio 1
    OP: 0.45          // its strength
  };

  /* `?west=` — read once, on the main thread. In a worker location.search
     is the worker script's, which is empty, and nothing in build() reads
     this: the FIELD ARRAY is identical under every setting.

     ==== R61 (a): THE SWELL IS THE SEA NOW. Tommy, 2026-09-14, on R46's
     three: "swell is great". So the DEFAULT — no query at all — is
     `swell`, the engraver's water-lining over the open sea, and

         ?west=0      today's flat sea, to the bit (R46's old default)
         ?west=bed    the generative seabed alone
         ?west=both   the seabed with the lining over it
         ?west=swell  the default, written out

     Nothing about the FIT moves: the swell is gated by `seaW`, which is
     exactly 0 at January 2022 and east of it, so the record's own sheet
     is the sheet it was (measured: 0 px, R61 report §a). `Room.bedAt` is
     likewise untouched — the seabed is still off unless it is asked for,
     so the voyage's lead and its grounding are R46's `?west=0` numbers.
     A worker that cannot read the query gets the flat sea, as before;
     nothing in build() reads WEST, so the array is the same either way.
     ==== */
  var WEST = (function () {
    var q;
    try { q = new URLSearchParams((root.location && root.location.search) || ''); }
    catch (e) { return { bed: false, swell: false, amp: 0, mean: 0, sp: 0, raw: '' }; }
    var vq = q.get('west');
    var v = String(vq === null || vq === '' ? 'both' : vq).toLowerCase();  /* Tommy, 2026-09-14 evening: "let's use both as the default" */
    var num = function (k, d) { var n = parseFloat(q.get(k)); return isFinite(n) ? n : d; };
    return {
      raw: v,
      bed:   v === 'bed'   || v === 'both',
      swell: v === 'swell' || v === 'both',
      amp:  num('westamp',  SEABED.AMP),
      mean: num('westmean', SEABED.MEAN),
      sp:   num('westsp',   SEABED.SP0)
    };
  })();

  /* The GLSL. uSeabed is (amp, mean, 1/lobe1, 1/lobe2) and uSeabed2 is
     (1/lobe3, the surveyed west start in map x, the gate's run, 0).
     AMP 0 MEANS NO SEABED, and both uniforms default to (0,0,0,0), so any
     program that never receives them draws the flat floor it always drew.

     The gate is the SAME RUN the field's own west crossfade uses — R3
     (c)'s OPEN_SEA, three quarters of a plate-height — so the seabed
     grows out of the record's western shore over the same distance the
     floor itself does, and there is no step at January 2022. It is
     exactly 0 at the line and east of it. */

  /* ==================================================================
     V3 — THE CHART ENDS AT JANUARY 1986, AND A COAST STANDS THERE

     Tommy (EGGS.md section 4): *"cut the left-hand sides of the map off at
     January 1986. Is there any reason to go back to 1982? ... once you pan
     the map, you can see beyond where the map cuts off."* And of what the
     land is: *"some land that's almost like a torso, so I'm being born out
     of it ... unexpected and get a chuckle"*, with the figure running off
     the sheet the way the Leo Belgicus of 1583 runs off its own.

     So the western end of the survey is no longer open water with a fence
     across it at March 1981. A COAST stands at January 1986, the pan stops
     at the paper's edge (engine.js clampView), and the land runs on off
     the map because the paper is cut there.

     WHY IT IS HERE AND NOT IN A DRAWING. The card offered two
     constructions: raise the ground and let the sheet draw its own coast,
     or draw the land with lm-art's pen. The ground is right — the tint
     bands, the coast line, the foreshore and the hachure are the sheet's
     own hand and a pen drawing of them would be a second, disagreeing
     hand — and it is also the only one the keel and the lead can read,
     because `Room.bedAt` is voyVec/voyGroundJS and nothing else.

     WHY IT RIDES IN `uVoyBank.x`. The coast needs ONE number the fragment
     shader does not have: the map x of 1 January 1986. It cannot be a
     literal (the sheet's epoch is struck from the record's own year, so
     every map x moves each new year), it cannot come from the field
     object (this GLSL is a module-scope string, built before any field
     exists), and it cannot have a uniform of its own, because the only
     hand that sets uniforms is engine.js's and this card may not touch
     it. What IS ours is the four floats engine.js uploads out of
     `voyGround` — and of those, `fair` is a CONSTANT of this file (2,700)
     that never needed to travel at all. So the fairway's half-width is a
     literal in the arithmetic below and `uVoyBank.x` carries the cut.
     `uVoyFoul`'s eight slots could not take it: they are exactly full
     with the seven marks' eight domes (REPORT-room-V2 OPEN 2).
     (OB1, 2026-09-21: the array is twenty-eight now and the marks' eight
     domes share it with the two unnamed dangers' fifteen — see `FOUL_N`
     above. The reasoning for `uVoyBank.x` is unchanged: a slot in that
     array is a DOME, and the paper's west edge is not one.)

     THE SHAPE is a signed distance field — a ribbon of land along the
     paper's edge, a few capsules smooth-joined into it for the figure,
     and the BERTH's dock cut back out of them. `?berth=a|b|c` picks the
     figure and NOTHING ELSE: the cut, the clamp, the ribbon, the dock and
     the quay wall are the same in all three, because they are the card's
     and not taste. All numbers are MAP UNITS, x measured east of the cut
     and y north(-)/south(+) of the fairway's middle.
     ================================================================== */
  var CUT_ISO = '1986-01-01';           /* where the paper is cut */
  var VOY_FAIR = 2700;                  /* the fairway's half-width, a literal now */
  /* ==== OB1 (2026-09-21): AND THE FOUL-GROUND ARRAY IS NO LONGER EIGHT.
     It was eight because the seven marks raised exactly eight domes and
     not one slot more (the V3 note above, and REPORT-room-V2 OPEN 2 under
     it, both say so in as many words). Tommy, of the two long empty legs:
     *"put an unnamed danger on each ... a reef between 2009 and 1998 and a
     bank tongue between 1994 and 1986, laid across the pencil line so the
     reader has to leave it and come back."* A danger laid ACROSS the line
     is a RIDGE, and a ridge in this arithmetic is a chain of domes: the
     field squashes every dome 1.6 to 1 across, so one circle is always
     longer along the sea than it is across it, and the only way to make a
     bar that is wider than it is long is to lay several of them side by
     side. The reef is five and the bank tongue is ten, which is fifteen
     on top of the marks' eight: TWENTY-THREE of twenty-eight, and the
     five spare are deliberate — an array that is EXACTLY full is what the
     note below this one was written about, and it is not a state to go
     back to.

     THE COST, because this file is the one that pays it: the fragment
     loop is bounded by the array and BREAKS at `uVoyFoulN`, so a sheet
     with no voyage data still runs zero iterations and the sea west of
     2022 now runs twenty-three instead of eight. Each of them is a
     subtract, an abs and a compare before it continues — the sqrt is only
     reached inside a dome's own x band, which no fragment but the ones
     over a danger ever are. 28 vec4 is 112 floats of the fragment's
     uniform budget, well inside GLES 3.00's guaranteed 896 components.
     Grow this number and the shader, the JS mirror and engine.js's upload
     all follow it; they read it and nothing is written twice. ==== */
  var FOUL_N = 28;
  var VCO = {
    RIB: 74,        /* the ribbon of land along the cut, before the wander;
                       (a) sets it off — see VCO_RIB */
    RA1: 34, RL1: 611, RA2: 17, RL2: 197, RPH: 1.1,   /* its two sines */
    JOIN: 40,       /* how softly the figure joins the ribbon (smooth min) */
    SHORE: 150,     /* how far offshore the ground is still coming up */
    INLAND: 260,    /* how far in the land goes on rising (V5: a leg is
                       four hundred units thick, and 430 left one all foreshore) */
    RISE: 2.42,     /* bed at the shore: floor -2.129 + 2.42 = +0.29, land */
    HILL: 1.15,     /* and +1.44 inland, which is what the hachure reads */
    /* ==== V6 (review): THE DOCK AND ITS WALL ARE STRUCK OUT. They were
       `WALL: 26` and `DOCK: [430,-380,790,-376,40]` — a slot dredged back
       out of the land with stone along its north side. Tommy, shown it in
       the legs: *"I still think the actual berth ruins the vibe. The small
       indentation doesn't really fit the vibe."* Ruling: *"yes, for now: I
       don't want to make it overly sexual, just not weird, and we can
       refine later."* So nothing is cut out of the coast at all any more:
       the shore either side of the berth is the smooth profile of the
       thighs with no notch in it, and she lies in the OPEN WATER between
       them, made fast to one post standing in it (voyage.js). ==== */
  };
  /* THE THREE FIGURES. (a) is Tommy's own words and the default. */
  var VCO_SEG = {
    /* ==== FOLLOW-UP (4): (a) HAS TO READ. The V3 reviewer, of the first
       cut: *"It does not read as a torso on a first look, and only weakly
       on a deliberate squint … no discernible neck, chest, or head
       silhouette."* He was right, and the fault was not the capsules — it
       was THE RIBBON. A strip of land ran the whole height of the basin
       along the cut, so the figure was a bulge on a wall of land and the
       NECK, which is the one part of it that has to leave the sheet, had
       nothing to leave. `VCO_RIB` turns the ribbon off for (a) alone: the
       sea comes right to the paper's edge above the shoulder and below
       the arm, the neck is the only land that crosses the cut, and the
       head is beyond it because the chart is cut there.

       WHAT THE SILHOUETTE IS, west to east, at the one thing this 3.4:1
       window can carry — an east-facing profile: a NECK of 190 units
       crossing the paper's edge, a SHOULDER that steps out to 450 in
       eighty units, the CHEST's curve, a WAIST nipped to 360, the HIP
       broad again, and the ARM lying along the south flank with the crook
       between arm and chest open to the east. The quay is in that crook.
       (b) and (c) are untouched but for their beacon. ==== */
    a: [[-420, -562, 150, -559, 58],   /* the neck, 116 across, and the head is off the map */
        [300, -560, 640, -557, 140],   /* the shoulder steps out to 280, and the chest */
        [640, -557, 900, -554, 112],   /* the waist, nipped to 224 */
        [900, -575, 1140, -572, 132],  /* the hip, broad again */
        [270, -450, 340, -390, 50],    /* the arm leaves the shoulder */
        [350, -298, 980, -304, 42]],   /* the arm along the flank; the crook is the berth */
    /* (b) A PLAIN COAST: a bay with a low headland either side of it, the
       quay at the bay's north shore. */
    b: [[-350, -690, 860, -650, 250],  /* the north headland; its south shore is the quay */
        [-350, 430, 700, 400, 230]],   /* the south headland, lower and further out */
    /* (c) A HEADLAND with the quay on its lee side and a beacon at the
       point (voyage.js stands a perch on the last capsule below). */
    c: [[-350, -690, 1150, -650, 250], /* the headland */
        [1150, -650, 1420, -632, 105]] /* its point, where the beacon stands */
  };
  /* the ribbon, per variant: (b) and (c) keep theirs; (a) has no ribbon
     at all any more — its coastline IS the figure, drawn by VCO_PROF. */
  var VCO_RIB = { a: -900, b: VCO.RIB, c: VCO.RIB };

  /* ==================================================================
     V5 — THE LOWER HALF, THE LEGS, AND THE BERTH BETWEEN THE THIGHS

     Tommy, shown V4's neck-to-thigh coast: what the land is, in his own
     words, is *"the lower half of a woman on her back, hips at the cut,
     two female legs sticking out to the east into the sea, the berth
     between the thighs, the boat arriving head-first from the east —
     that's me, the baby being born."*

     So V4's figure is struck out entire. The breast, the waist and the
     neck are gone; the CUT IS THE HIPS, and what stands east of it is two
     limbs and the water between them.

     THE CONSTRUCTION DOES NOT CHANGE, and the reason it does not is worth
     writing down, because it looks at first as though it must. A profile
     is the east shore as a single function of northing, prof(y), and a
     single-valued prof(y) cannot draw two bodies of land with water
     between them SIDE BY SIDE ALONG x. But these two limbs are not side
     by side along x: they are elongated EAST and separated in NORTHING,
     so at any one northing the land is still one unbroken run west of one
     shore — far east over a leg, back at the hips over the berth. Two
     peninsulas and a bay between them is exactly what a comb of one
     profile draws. Nothing is subtracted, no second lobe list is needed,
     and the keel, the lead, the tint bands and the hachure go on reading
     the one surface they always read.

     WHAT THE SILHOUETTE IS, north to south: sea to the paper's edge; the
     HIP coming out of it at the cut; the north leg leaving the hip and
     running 2,810 units EAST, tapering from a 590-unit thigh to a rounded
     FOOT; the BERTH, open to the sea eastward, 660 units across at the
     crotch and 1,120 between the feet; the south leg, the mirror of the
     north; and the south hip going back into the sea. The whole figure is
     2,160 units north to south, which is inside the 2,184.8 the widest
     view can carry, and 2,810 east, a third of that view's 8,480 — so all
     of it reads in one frame, which V4's 451-unit figure never did.

     THE BERTH IS BETWEEN THE THIGHS. The dock is a slot dredged into the
     berth's HEAD at the crotch, on the north thigh's inner flank, and the
     quay stands on that flank with 12 April 1986 (x 577.1) on it. She
     comes up the fairway due west, in between the feet, up the berth, and
     is made fast head-first at the crotch.

     THE TABLE, in map units: y is north(-)/south(+) of the fairway's
     middle, x is east of the cut. -1400 is "no land at all" — far enough
     west that no tilt overshoot at the widest zoom can reach it.
     ================================================================== */
  /* ==== VY2 (2026-09-26): THE LEGS ARE LEGS. Tommy: "the "legs" for the
     berth headland would be better if they were more leg-like" — and, when
     the figure was first drawn, "I don't want to make it overly sexual,
     just not weird". V5's legs were a profile, and a profile can only
     TAPER: at each northing the land is everything west of one shore, so
     a leg drawn by it narrows monotonically from hip to toe and can never
     swell again below a knee or turn a foot. They read as two long cones.

     So the profile now draws only what it can draw well — the hips at the
     cut, the berth's head, the inner thighs and each thigh tapering to the
     KNEE — and below the knee each leg is a chain of tapered capsules
     (`VCO_LEGS`, smooth-unioned onto the profile by the same `JOIN` the
     old capsule figures used): the knee, the CALF swelling to 392 across,
     the shin tapering to a slim ANKLE of about 200, and a FOOT at the end,
     turned out and a little east the way feet fall when someone lies on
     their back, the toes rounded. Plain anatomy at the scale of a coast,
     nothing more. The crotch, the inner thighs and so the berth, the
     shore the name is lettered on and the water she lies in are the V5
     numbers exactly: nothing west of x 1180 or inside |y| 420 moved. ==== */
  var VCO_PROF = [
    [-1200, -1400],   /* north of the hip: sea to the paper's edge */
    [-1050,   250],   /* the hip comes up out of the sea at the cut */
    [-1000,   520],   /* THE HIP, at the cut, and the chart is cut there */
    [ -930,   560],   /* its south shoulder, where the leg leaves it */
    [ -895,  1000],   /* the thigh's outer shore, running east */
    [ -862,  1400],   /* tapering */
    [ -835,  1700],   /* to the knee, outside */
    [ -745,  1880],   /* the profile's own end, under the calf (VCO_LEGS) */
    [ -650,  1910],
    [ -555,  1870],
    [ -478,  1700],   /* the knee, inside */
    [ -420,  1180],   /* the inner thigh */
    [ -372,   920],   /* and its last run east, which is the quay's back */
    [ -330,   460],   /* THE CROTCH: the berth's head; the quay is on this flank */
    [    0,   440],   /* THE BERTH, between the thighs, open to the sea eastward */
    [  330,   460],   /* the crotch again, south side */
    [  372,   920],
    [  420,  1180],   /* the south inner thigh */
    [  478,  1700],
    [  555,  1870],
    [  650,  1910],
    [  745,  1880],
    [  835,  1700],
    [  862,  1400],
    [  895,  1000],
    [  930,   560],   /* back to the hip */
    [ 1000,   520],   /* THE HIP, south side */
    [ 1050,   250],
    [ 1200, -1400]    /* south of the hip: sea to the paper's edge again */
  ];
  /* VY2: below the knee, in map units east of the cut and north(-) of the
     fairway's middle: [x0, y0, x1, y1, r0, r1], the radius running from
     r0 to r1 along the segment. The north leg, then its mirror. */
  var VCO_LEGS = (function () {
    var N = [
      [1740, -656, 1990, -663, 166, 196],   /* the knee, and the calf swelling */
      [1990, -663, 2340, -674, 196, 130],   /* the shin, tapering */
      [2340, -674, 2590, -684, 130,  90],   /* to the ankle */
      [2590, -684, 2735, -870,  90, 102],   /* THE FOOT, turned out: heel to ball */
      [2735, -870, 2815, -1030, 102, 82]    /* and the toes */
    ];
    return N.concat(N.map(function (c) { return [c[0], -c[1], c[2], -c[3], c[4], c[5]]; }));
  })();
  /* the furthest east any of it reaches, plus the offshore blend: east of
     this there is provably no land, and the fragment can leave at once.
     At the widest pan two thirds of the sheet is east of it. */
  var VCO_PROF_E = VCO_LEGS.reduce(function (e, c) {
    return Math.max(e, c[0] + c[4], c[2] + c[5]); }, 1910) + VCO.SHORE + 10;
  /* and how far north and south the land goes, for the view voyage.js is
     left in when she is fast — it frames BOTH legs and the water between */
  VCO.PROF_E = VCO_PROF_E;
  VCO.PROF_N = 1130;   /* VY2: was 1090, the hips'; the turned-out toes reach 1,112 */
  /* VY2: the legs' mouth, the water between the feet, where she is put
     back after she has gone out at the berth (voyage.js `recast`) */
  VCO.MOUTH = 2700;
  /* WHAT VOYAGE.JS MOORS BY, now that there is no dock and no quay for it
     to read: the east shore itself. `profAt(dy)` is the profile above at
     one northing — the same chain the shader and coastJS run, in map units
     east of the cut — and it is all the berth needs, because the post she
     is made fast to stands in open water (voyage.js puts it off her stem)
     and the only ground her name can be lettered on is the inner thigh,
     which is wherever this function crosses the destination's meridian. */
  VCO.profAt = function (dy) {
    var v = VCO_PROF[0][1];
    for (var i = 1; i < VCO_PROF.length; i++)
      v += (VCO_PROF[i][1] - v) * smoothstep(VCO_PROF[i - 1][0], VCO_PROF[i][0], dy);
    return v;
  };


  /* V6 (review): ONE BERTH. `?berth=b|c` ALIAS (a) now — (b) and (c) were
     a plain bay and a headland built to stand the same quay against, and
     the quay is struck out, so there is nothing left for them to be.
     VCO_SEG / VCO_RIB / segGL / ribGL below are kept as the history of
     how this coast was drawn before it was a profile. */
  var BERTHQ = 'a';
  var VCO_ON = VCO_SEG[BERTHQ] || VCO_SEG.a;
  var VCO_RIB_ON = VCO_RIB[BERTHQ] !== undefined ? VCO_RIB[BERTHQ] : VCO.RIB;
  var VCO_PROF_ON = BERTHQ === 'a' ? VCO_PROF : null;
  function fx(v) { var s = (+v).toFixed(4); return s.indexOf('.') < 0 ? s + '.0' : s; }
  /* the same capsule, written out for the GPU once per segment: distance
     to a rounded segment, then SMOOTH-MINned into what is there already */
  function segGL(list) {
    var out = [];
    list.forEach(function (s, i) {
      out.push(
        '  {',
        '    vec2 a = vec2(' + fx(s[0]) + ',' + fx(s[1]) + '), b = vec2(' + fx(s[2]) + ',' + fx(s[3]) + ');',
        '    vec2 pa = p - a, ba = b - a;',
        '    float hh = clamp(dot(pa,ba)/dot(ba,ba), 0.0, 1.0);',
        '    vec2 qq = pa - ba*hh; float ll = max(length(qq), 1e-4);',
        /* VY2: a tapered one when it carries a second radius */
        (s.length > 5 && s[5] !== s[4]
          ? '    float dd = ll - (' + fx(s[4]) + ' + ' + fx(s[5] - s[4]) + '*hh);'
          : '    float dd = ll - ' + fx(s[4]) + ';'),
        '    float hm = clamp(0.5 + 0.5*(dd - d)/' + fx(VCO.JOIN) + ', 0.0, 1.0);',
        '    d = mix(dd, d, hm) - ' + fx(VCO.JOIN) + '*hm*(1.0 - hm);',
        '    g = normalize(mix(qq/ll, g, hm) + vec2(1e-6, 0.0));',
        '  }');
    });
    return out;
  }
  /* V4: (a)'s shore as a profile — the east coast as a function of
     northing, with its own slope, and the land is everything west of it.
     No branch: outside a step's span the smoothstep's slope is zero. */
  function profGL(list) {
    var out = ['  if (p.x > ' + fx(VCO_PROF_E) + ') return vec3(0.0);   /* V4: provably no land east of this */',
               '  float pv = ' + fx(list[0][1]) + ', pdv = 0.0, pt, pdt, pu;'];
    for (var i = 1; i < list.length; i++) {
      var y0 = list[i - 1][0], y1 = list[i][0], T = fx(list[i][1]);
      out.push('  pu = clamp((p.y - ' + fx(y0) + ')/' + fx(y1 - y0) + ', 0.0, 1.0);',
               '  pt = pu*pu*(3.0 - 2.0*pu); pdt = 6.0*pu*(1.0 - pu)/' + fx(y1 - y0) + ';',
               '  pdv = pdv*(1.0 - pt) + (' + T + ' - pv)*pdt; pv += (' + T + ' - pv)*pt;');
    }
    out.push('  float d = p.x - pv;',
             '  vec2 g = normalize(vec2(1.0, -pdv));');
    return out;
  }
  function ribGL() {
    return [
      '  /* the ribbon of land along the cut, wandering as a coast does */',
      '  float rib = ' + fx(VCO_RIB_ON) + ' + ' + fx(VCO.RA1) + '*sin(p.y/' + fx(VCO.RL1) + ')' +
        ' + ' + fx(VCO.RA2) + '*sin(p.y/' + fx(VCO.RL2) + ' + ' + fx(VCO.RPH) + ');',
      '  float d = p.x - rib;',
      '  vec2 g = normalize(vec2(1.0, -(' + fx(VCO.RA1 / VCO.RL1) + '*cos(p.y/' + fx(VCO.RL1) + ')' +
        ' + ' + fx(VCO.RA2 / VCO.RL2) + '*cos(p.y/' + fx(VCO.RL2) + ' + ' + fx(VCO.RPH) + '))));'
    ].concat(segGL(VCO_ON));
  }
  var COAST_GLSL = [
    '/* V3: the land west of the record, in BED units, with its gradient.',
    '   `sb` is the open sea\'s own floor at this point, so that the land\'s',
    '   top is flat where the blend is full, exactly as the banks are. */',
    'vec3 coastVec(vec2 s, vec3 sb){',
    '  float cx = uVoyBank.x;',
    '  if (cx > -1.0 || uVoyBank.z <= 0.0) return vec3(0.0);   /* no cut known, or ?vground=0 */',
    '  vec2 p = vec2(s.x - cx, s.y - uVoyBank.w);'
  ].concat(VCO_PROF_ON ? profGL(VCO_PROF_ON).concat(segGL(VCO_LEGS)) : ribGL()).concat([
    '  /* V6: nothing is cut back out of the land — the shore IS the profile */',
    '  float ts = 1.0 - sbSmst(0.0, ' + fx(VCO.SHORE) + ', d);',
    '  if (ts <= 0.0) return vec3(0.0);',
    '  float vv = clamp(d/' + fx(VCO.SHORE) + ', 0.0, 1.0);',
    '  float dts = (d > 0.0 && d < ' + fx(VCO.SHORE) + ') ? -6.0*vv*(1.0 - vv)/' + fx(VCO.SHORE) + ' : 0.0;',
    '  vec2 dt = dts*g;',
    '  float uu = clamp(-d/' + fx(VCO.INLAND) + ', 0.0, 1.0);',
    '  float sm = uu*uu*(3.0 - 2.0*uu);',
    '  float dsm = (d < 0.0 && uu < 1.0) ? -6.0*uu*(1.0 - uu)/' + fx(VCO.INLAND) + ' : 0.0;',
    '  float top = ' + fx(VCO.RISE) + ' + ' + fx(VCO.HILL) + '*sm;',
    '  vec2 dtop = ' + fx(VCO.HILL) + '*dsm*g;',
    '  float up = max(top - sb.x, 0.0);',
    '  if (up <= 0.0) return vec3(0.0);',
    '  float t = ts;',
    '  return vec3(up*t, (dtop.x - sb.y)*t + up*dt.x, (dtop.y - sb.z)*t + up*dt.y);',
    '}'
  ]).join('\n');

  var SEABED_GLSL = [
    'uniform vec4 uSeabed;   /* R46: amp, mean, 1/lobe1, 1/lobe2 */',
    'uniform vec4 uSeabed2;  /* R46: 1/lobe3, west start, gate run, 0 */',
    'float sbSmst(float a, float b, float v){ float t = clamp((v-a)/(b-a),0.0,1.0); return t*t*(3.0-2.0*t); }',
    '/* the west gate, and its slope in x (the ink on the plane wants the',
    '   first and the bed wants both, so that the waterlining does not kink',
    '   where the convention fades in) */',
    'float seaW(vec2 s){',
    '  if (uSeabed2.z <= 0.0) return 0.0;',
    '  return sbSmst(0.0, uSeabed2.z, max(uSeabed2.y - s.x, 0.0));',
    '}',
    'float seaWdx(vec2 s){',
    '  if (uSeabed2.z <= 0.0) return 0.0;',
    '  float d = max(uSeabed2.y - s.x, 0.0);',
    '  if (d <= 0.0 || d >= uSeabed2.z) return 0.0;',
    '  float t = d/uSeabed2.z;',
    '  return -6.0*t*(1.0 - t)/uSeabed2.z;      /* d(w)/d(s.x) */',
    '}',
    '/* (bed, d/dsx, d/dsy) of the open sea\'s floor, in BED units, gated */',
    'vec3 seabedVec(vec2 s){',
    '  if (uSeabed.x <= 0.0 || uSeabed2.z <= 0.0) return vec3(0.0);',
    '  float w = seaW(s);',
    '  if (w <= 0.0) return vec3(0.0);',
    '  vec2 o = s - uLat.xy;       /* the hash against the lattice origin */',
    '  vec3 n1 = tVnoise(o*uSeabed.z);',
    '  vec3 n2 = tVnoise(o*uSeabed.w + 31.4);',
    '  vec3 n3 = tVnoise(o*uSeabed2.x + 7.13);',
    '  float n  = ' + SEABED.W1 + '*n1.x + ' + SEABED.W2 + '*n2.x + ' + SEABED.W3 + '*n3.x;',
    '  float gx = ' + SEABED.W1 + '*n1.y*uSeabed.z + ' + SEABED.W2 + '*n2.y*uSeabed.w + ' + SEABED.W3 + '*n3.y*uSeabed2.x;',
    '  float gy = ' + SEABED.W1 + '*n1.z*uSeabed.z + ' + SEABED.W2 + '*n2.z*uSeabed.w + ' + SEABED.W3 + '*n3.z*uSeabed2.x;',
    '  float b  = uSeabed.y + uSeabed.x*(2.0*n - 1.0);',
    '  return vec3(w*b,',
    '              w*2.0*uSeabed.x*gx + seaWdx(s)*b,',
    '              w*2.0*uSeabed.x*gy);',
    '}',
    'float seabedOf(vec2 s){ return seabedVec(s).x; }',
    '',
    '/* ==== R46: THE SWELL — THE SHEET\'S OWN HAND, NOT A RIPPLE.',
    '',
    '   The engraver\'s water-lining is the convention this is: fine crests',
    '   that follow the coast and open out to sea. West of January 2022 the',
    '   coast IS the record\'s western neat line, so a crest is a line of',
    '   constant distance west of it and the spacing GROWS with that',
    '   distance, sp(d) = SP0 x (1 + d/OPEN). The phase whose gradient is',
    '   exactly 1/sp is therefore a logarithm,',
    '        phase(d) = (OPEN/SP0) x ln(1 + d/OPEN)',
    '   and d(phase)/dd = 1/sp(d) analytically, everywhere — which is what',
    '   lets the width be drawn without a derivative of a derivative.',
    '',
    '   IT IS GROUND INK, NOT INK ON THE PLANE. It was built on the water',
    '   plane first, and the plane IS NOT DRAWN AT PLAN (water.js',
    '   waterOpacity, relief.js :5356 — a Fresnel curve on the tilt alone,',
    '   0 below a degree), so the whole convention vanished at the fit, and',
    '   the flat fit dragged west is one of the four views this is for. The',
    '   sheet\'s own eight waterlines are ground ink and are seen THROUGH',
    '   the plane at tilt; the swell is the same convention, so it is drawn',
    '   in the same place, beside them, and the plane mutes it by exactly',
    '   as much as it mutes them.',
    '',
    '   IT MUST NOT SHIMMER UNDER PAN, which is two separate promises.',
    '   (1) THE WIDTH IS MEASURED THROUGH THE JACOBIAN, NOT fwidth. R1 (b)',
    '   found this on the contours: fwidth and the geometric mean of the',
    '   two screen scales are wrong by a factor of five under a tilt, and',
    '   wrong by a DIFFERENT factor every frame of a turn, so the lines do',
    '   not move but their widths do, which the eye reads as crawl. The map',
    '   point is linear across a triangle, so J = mat2(dFdx(s), dFdy(s)) is',
    '   exact and |J^T grad(phase)| is the phase a crest changes by per',
    '   device pixel along the direction that matters.',
    '   (2) NOTHING FINER THAN THE SCREEN IS EVER DRAWN. The spacing is in',
    '   MAP units, so far back or zoomed out a crest period falls under a',
    '   pixel, and a pattern at that density is aliasing whatever it is',
    '   made of. So the ink fades out over 2.2 -> 6 device pixels PER',
    '   CREST — a continuous function of the same Jacobian, so it eases',
    '   rather than pops.',
    '',
    '   The crests are not ruled: two sines in y, 1400 and 560 map units',
    '   long, wander the distance by about +/-39 units and tilt a crest by',
    '   at most 13 degrees. They never cross, because the meander shifts',
    '   every crest at a given y by the same amount. Their slope is',
    '   analytic too and goes into the same gradient. ==== */',
    'uniform vec4 uSwell;    /* R46: SP0, 1/OPEN, width (device px), strength */',
    'uniform vec4 uSwell2;   /* R46: A1, 2pi/L1, A2, 2pi/L2 */',
    'float swellInk(vec2 m, mat2 J, float dpr){',
    '  if (uSwell.x <= 0.0) return 0.0;',
    '  float g = seaW(m);',
    '  if (g <= 0.002) return 0.0;',
    '  float K  = 1.0/uSwell.y;',
    '  float d0 = max(uSeabed2.y - m.x, 0.0);',
    '  float mw = uSwell2.x*sin(m.y*uSwell2.y) + uSwell2.z*sin(m.y*uSwell2.w + 1.7);',
    '  float dm = uSwell2.x*uSwell2.y*cos(m.y*uSwell2.y) + uSwell2.z*uSwell2.w*cos(m.y*uSwell2.w + 1.7);',
    '  float d  = max(d0 + mw, 0.0);',
    '  float dC = ' + (SEABED.CAP - 1) + '*K;      /* where the opening stops */',
    '  float dd = min(d, dC);',
    '  float sp = uSwell.x*(1.0 + dd/K);         /* the spacing here, map units */',
    '  float ph = (K/uSwell.x)*log(1.0 + dd/K) + max(d - dC, 0.0)/(uSwell.x*' + SEABED.CAP + ');',
    '  vec2  gp = vec2(-1.0, dm)/sp;             /* d(phase)/d(map) */',
    '  float gpx = max(length(vec2(dot(J[0], gp), dot(J[1], gp))), 1e-7);',
    '  float lod = sbSmst(2.2, 6.0, 1.0/gpx);    /* device px per crest */',
    '  float dpx = abs(fract(ph) - 0.5)/gpx;',
    '  float w = uSwell.z*dpr;',
    '  return clamp(w*0.5 - dpx + 0.5, 0.0, min(1.0, w))*uSwell.w*g*lod;',
    '}',
    '',
    /* X14 (g): THE VOYAGE'S OWN GROUND — the fairway's banks and the
       foul ground round each mark, west of January 2022, analytic, with
       its own gradient, and read in JS by voyGroundJS below so that
       `Room.bedAt` and the depth tints are ONE surface. The reasoning is
       where the rest of the voyage's is, in voyage.js's X14 block, which
       is fetched at idle; this file carries the arithmetic only. */
    'uniform vec4 uVoyBank;    /* X14: fair, run, rise, ymid */',
    'uniform vec4 uVoyFoul[' + FOUL_N + ']; /* X14: x, y, radius, rise */',
    'uniform float uVoyFoulN;',
    /* V3: uVoyBank.x is NO LONGER the fairway's half-width. It carries the
       map x of 1 January 1986 — the paper's west edge — because the coast
       needs it and there is no other float engine.js will carry for us.
       See the V3 block above `CUT_ISO`. The half-width is a literal. */
    COAST_GLSL,
    '/* (bed, d/dsx, d/dsy), in BED units, gated west. rise is a height the',
    '   ground comes UP TO over the floor: r = (rise - seabed)*t. */',
    'vec3 voyVec(vec2 s){',
    '  if (uVoyBank.z <= 0.0 && uVoyFoulN < 0.5) return vec3(0.0);',
    '  float d = uSeabed2.y - s.x;',
    '  if (d <= 0.0) return vec3(0.0);',
    '  float gt = clamp(d/300.0, 0.0, 1.0);',
    '  float w = gt*gt*(3.0 - 2.0*gt);',
    '  float dw = (d < 300.0) ? -6.0*gt*(1.0 - gt)/300.0 : 0.0;',
    '  vec3 sb = seabedVec(s);',
    '  float r = 0.0, rx = 0.0, ry = 0.0;',
    '  if (uVoyBank.z > 0.0) {',
    '    float ay = abs(s.y - uVoyBank.w);',
    '    float bt = clamp((ay - ' + fx(VOY_FAIR) + ')/uVoyBank.y, 0.0, 1.0);',
    '    float t = bt*bt*(3.0 - 2.0*bt);',
    '    float dt = (ay > ' + fx(VOY_FAIR) + ' && ay < ' + fx(VOY_FAIR) + ' + uVoyBank.y)',
    '             ? 6.0*bt*(1.0 - bt)/uVoyBank.y*sign(s.y - uVoyBank.w) : 0.0;',
    '    float up = max(uVoyBank.z - sb.x, 0.0);',
    '    r = up*t; rx = -sb.y*t; ry = -sb.z*t + up*dt;',
    '  }',
    '  for (int i = 0; i < ' + FOUL_N + ' + uZero; i++) {',
    '    if (float(i) >= uVoyFoulN) break;',
    '    vec4 f = uVoyFoul[i];',
    '    float fx = s.x - f.x;',
    '    if (abs(fx) >= f.z) continue;',
    '    float fy = (s.y - f.y)*1.6;',
    '    float dd = sqrt(fx*fx + fy*fy);',
    '    if (dd >= f.z) continue;',
    '    float ft = clamp((dd - f.z*0.35)/(f.z*0.65), 0.0, 1.0);',
    '    float t = 1.0 - ft*ft*(3.0 - 2.0*ft);',
    '    float up = max(f.w - sb.x, 0.0);',
    '    float h = up*t;',
    '    if (h <= r) continue;',
    '    float g = (dd > f.z*0.35 && dd > 1e-4)',
    '            ? -up*6.0*ft*(1.0 - ft)/(f.z*0.65)/dd : 0.0;',
    '    r = h; rx = g*fx - sb.y*t; ry = g*fy*1.6 - sb.z*t;',
    '  }',
    '  /* V3: and the coast at the paper\'s edge, over everything else */',
    '  vec3 cc = coastVec(s, sb);',
    '  if (cc.x > r) { r = cc.x; rx = cc.y; ry = cc.z; }',
    '  return vec3(w*r, w*rx + dw*r, w*ry);',
    '}',
    ''
  ].join('\n');

  /* The local sea is three normal sailing-window WIDTHS across and still
     bounded: 2,700 units of open water either side of its middle, then a
     450-unit bank, for 6,300 total. The same four values feed the fragment
     shader, voyGroundJS and engine clamp; the boat never navigates a
     different coast from the one the paper draws. */
  var VOY = { FAIR: VOY_FAIR, RUN: 450, RISE: 2.0, FADE: 300, FOULN: FOUL_N };

  /* the one table both sides read; voyage.js fills `foul` when its data lands.

     ==== V3: `fair` IS THE CUT NOW, AND IT IS A GETTER. engine.js uploads
     `uVoyBank = (fair, run, rise, ymid)` and this file owns three of the
     four. The fairway's half-width never varied, so it has become a
     literal in the arithmetic on both sides and the slot carries the one
     number the coast cannot do without: the map x of 1 January 1986,
     struck by the sheet's own arithmetic off the record's epoch (which
     moves with the record's year, so it cannot be written down). It is
     read from whatever field is in hand — the one `voyGroundJS` is given,
     or the engine's own — and is 0, which the shader reads as "no cut
     known", until a real field with an epoch has landed. ==== */
  var cutSeen = { ep: 0, x: 0 };
  function cutOf(f) {
    if (!f || !f.days || !f.days.epochMs) return 0;
    if (cutSeen.ep !== f.days.epochMs) {
      cutSeen.ep = f.days.epochMs;
      cutSeen.x = C.px + ((Date.parse(CUT_ISO + 'T00:00:00Z') - f.days.epochMs) /
                          (7 * 86400000)) * C.cw;
    }
    return cutSeen.x;
  }
  var VOYG = { run: VOY.RUN, rise: VOY.RISE, foul: [] };
  Object.defineProperty(VOYG, 'fair', { enumerable: true, get: function () {
    return cutOf(root.Room && root.Room.fld);
  } });
  /* `?vground=0` — the measuring pair for (g), not a design switch. */
  try {
    if (/[?&]vground=0\b/.test((root.location && root.location.search) || '')) {
      VOYG.rise = 0; VOY.OFF = 1;
    }
  } catch (e) { }

  /* the same function in JS, for `Room.bedAt` (day.js) */
  function voyGroundJS(f, mx, my) {
    if (!f || !f.front || VOYG.rise <= 0 && !VOYG.foul.length) return 0;
    var d = f.front.west - mx;
    if (d <= 0) return 0;
    var gt = d >= VOY.FADE ? 1 : d / VOY.FADE, w = gt * gt * (3 - 2 * gt);
    var sb = seabedJS(f, mx, my);            /* the same noise the shader reads */
    var ymid = (f.front.c0 + f.front.c1) / 2;
    var ay = Math.abs(my - ymid);
    var bt = clamp((ay - VOY.FAIR) / VOYG.run, 0, 1);
    var r = Math.max(VOYG.rise - sb, 0) * bt * bt * (3 - 2 * bt);
    for (var i = 0; i < VOYG.foul.length && i < FOUL_N; i++) {
      var o = VOYG.foul[i];
      if (!o[2]) continue;
      var fx = mx - o[0];
      if (Math.abs(fx) >= o[2]) continue;
      var fy = (my - o[1]) * 1.6;
      var dd = Math.sqrt(fx * fx + fy * fy);
      if (dd >= o[2]) continue;
      var ft = clamp((dd - o[2] * 0.35) / (o[2] * 0.65), 0, 1);
      var h = Math.max(o[3] - sb, 0) * (1 - ft * ft * (3 - 2 * ft));
      if (h > r) r = h;
    }
    /* V3: and the coast at the paper's edge — the SAME arithmetic the
       shader runs, off the same table, so the keel, the lead and the
       tint bands read one surface (X14 g's rule). */
    var cc = coastJS(f, mx, my, sb);
    if (cc > r) r = cc;
    return w * r;
  }

  /* the capsules smooth-unioned onto a distance, as `segGL` writes them
     for the GPU (VY2: tapered when a segment carries a second radius) */
  function segJS(L, px, py, d) {
    for (var i = 0; i < L.length; i++) {
      var s0 = L[i];
      var pax = px - s0[0], pay = py - s0[1], bax = s0[2] - s0[0], bay = s0[3] - s0[1];
      var hh = clamp((pax * bax + pay * bay) / (bax * bax + bay * bay), 0, 1);
      var qx = pax - bax * hh, qy = pay - bay * hh;
      var r = s0.length > 5 ? s0[4] + (s0[5] - s0[4]) * hh : s0[4];
      var dd = Math.max(Math.sqrt(qx * qx + qy * qy), 1e-4) - r;
      var hm = clamp(0.5 + 0.5 * (dd - d) / VCO.JOIN, 0, 1);
      d = dd + (d - dd) * hm - VCO.JOIN * hm * (1 - hm);
    }
    return d;
  }
  /* V3: the coast, in JS. `coastVec` in COAST_GLSL above is this function
     unrolled for the GPU; if one is changed the other must be. */
  function coastJS(f, mx, my, sb) {
    var cx = cutOf(f);
    if (cx >= -1 || VOYG.rise <= 0) return 0;
    var px = mx - cx, py = my - (f.front.c0 + f.front.c1) / 2;
    var d, i;
    if (VCO_PROF_ON) {
      /* V4: (a)'s coastline is a profile — the same chain the shader runs */
      if (px > VCO_PROF_E) return 0;
      var L2 = VCO_PROF_ON, pv = L2[0][1];
      for (i = 1; i < L2.length; i++)
        pv += (L2[i][1] - pv) * smoothstep(L2[i - 1][0], L2[i][0], py);
      d = segJS(VCO_LEGS, px, py, px - pv);   /* VY2: and the legs below the knee */
    } else {
      var rib = VCO_RIB_ON + VCO.RA1 * Math.sin(py / VCO.RL1) +
                VCO.RA2 * Math.sin(py / VCO.RL2 + VCO.RPH);
      d = segJS(VCO_ON, px, py, px - rib);
    }
    var ts = 1 - smoothstep(0, VCO.SHORE, d);
    if (ts <= 0) return 0;
    var uu = clamp(-d / VCO.INLAND, 0, 1);
    var top = VCO.RISE + VCO.HILL * uu * uu * (3 - 2 * uu);
    return Math.max(top - sb, 0) * ts;
  }


  /* the same field in JS, float32 at every step so it is the shader's
     arithmetic and not a double's — `Room.bedAt` reads this, which is what
     puts real soundings under the voyage's lead (X5 OPEN 5). */
  function seabedJS(f, mx, my) {
    if (!WEST.bed || !f || !f.margin) return 0;
    var run = f.margin.openSea || 0;
    if (run <= 0) return 0;
    var d = f.front.west - mx;
    if (d <= 0) return 0;
    var t = d >= run ? 1 : d / run, w = t * t * (3 - 2 * t);
    var PA = f.front.c1 - f.front.c0;
    var ox = f32(mx - f.lat.X0), oy = f32(my - f.lat.Y0);
    var i1 = f32(1 / (SEABED.LOBE1 * PA)), i2 = f32(1 / (SEABED.LOBE2 * PA)),
        i3 = f32(1 / (SEABED.LOBE3 * PA));
    var n1 = vnoiseJS(f32(ox * i1), f32(oy * i1));
    var n2 = vnoiseJS(f32(f32(ox * i2) + 31.4), f32(f32(oy * i2) + 31.4));
    var n3 = vnoiseJS(f32(f32(ox * i3) + 7.13), f32(f32(oy * i3) + 7.13));
    var n = f32(f32(f32(SEABED.W1 * n1) + f32(SEABED.W2 * n2)) + f32(SEABED.W3 * n3));
    return w * f32(WEST.mean + f32(WEST.amp * f32(2 * n - 1)));
  }

  /* ==== JG2 (2026-09-26): THE WALK BLURS EVERY ROW BY WHAT THAT ROW ASKS
     FOR, NOT IN WHOLE-CELL LUMPS. Tommy, at #1385,568,706,48,-57:
     "contours are better but some are now perfectly smooth and others
     resemble what they did before". The ones that did were on the steep
     east bank of Devon Sound where it runs out past the record's line
     into the margin. JG1's read is smooth; the DATA there was not. The
     walk below used to round each row's blur to a whole-cell box
     (stepRadius, r(r+1)/3) and skip any row that needed less than 0.34 of
     a cell's variance, so the rows came in pairs: one blurred, the next
     a copy of it, then a jump. Measured on the lattice at x 1385-1415:
     rows 352/353, 357/358, 359/360, 361/362 equal to 0.01-0.05 and the
     row after each pair a step of up to 0.35 of bed. Every level laid on
     that surface is a staircase with a two-row tread (7.1 map units),
     and on a bank that crowds a dozen levels into 30 units each of them
     steps together. A box of FRACTIONAL half-width m + f (the two end
     taps weighted f) has a variance continuous in f, so each row now
     takes exactly the variance its distance asks for and no more: the
     ground eases out as the distance does, row by row. The ends are held
     (the same clamp boxBlurH uses).
     MEASURED at his hash, every level's crossing of the bank at y 700-850
     against a local quadratic (the short-period wobble alone): east bank
     0.021 map units rms, worst level 0.047, before; 0.0009 / 0.0028 now —
     the record's own stretch of the same bank reads 0.0001. The west bank
     0.023 / 0.053 to 0.003 / 0.008. Not the filter: clamping fieldAt to
     its four central samples moved none of it, and the bank inside the
     record, where the mesh and fieldAt already agree, was smooth before.
     The record's rows and the west margin are unchanged to the bit; the
     margin north and south moves by 0.0003 of bed on average, 0.23 at
     worst, where a steep bank crosses the line. ==== */
  function blurVar(src, dst, n, v) {
    /* the smallest whole box whose variance covers v, then the end taps'
       weight that lands it on v exactly */
    var m = 0;
    while ((m + 1) * (m + 2) / 3 < v) m++;
    var S0 = 2 * m + 1, M0 = m * (m + 1) * (2 * m + 1) / 3;
    var f = (v * S0 - M0) / (2 * (m + 1) * (m + 1) - 2 * v);
    if (f < 0) f = 0; else if (f > 1) f = 1;
    var inv = 1 / (S0 + 2 * f), sum = 0, i;
    for (i = -m; i <= m; i++) sum += src[clamp(i, 0, n - 1)];
    for (i = 0; i < n; i++) {
      dst[i] = (sum + f * (src[clamp(i - m - 1, 0, n - 1)] + src[clamp(i + m + 1, 0, n - 1)])) * inv;
      sum += src[clamp(i + m + 1, 0, n - 1)] - src[clamp(i - m, 0, n - 1)];
    }
  }

  function continueField(bed, field, lat, front, COAST, seaWest) {
    var W = field.W, H = field.H, DX = lat.DX, DY = lat.DY;
    var PA = front.c1 - front.c0;              // a plate-height, in map units
    var RUN = BEY.SINK * PA;                   // how far the margin has to carry
    /* the record's own cell bounds in the OLD lattice, and how much pad is
       already there — `smooth()` left real values in it, so the margin only
       has to make up the difference */
    var oy0 = Math.ceil((front.c0 - lat.Y0) / DY);
    var oy1 = Math.floor((front.c1 - lat.Y0) / DY);
    var ox0 = Math.ceil((front.west - lat.X0) / DX);
    var want = Math.ceil(RUN / DY) + 8, MY = Math.max(0, want - oy0);
    var wantX = Math.ceil(RUN / DX) + 8, MX = Math.max(0, wantX - ox0);
    if (H + MY > 4096 || W + MX > 8192) { MY = 0; MX = 0; }   // a driver's limit, not ours

    var W2 = W + MX, H2 = H + MY * 2, MX2 = MX, MY2 = MY;
    var X0 = lat.X0 - MX * DX, Y0 = lat.Y0 - MY * DY;
    var b2 = new Float32Array(W2 * H2);
    for (var y = 0; y < H; y++) b2.set(bed.subarray(y * W, y * W + W), (y + MY) * W2 + MX);
    /* the rows above and below the blit, and the columns west of it, start
       at the value of the nearest real row — the walk overwrites them all */
    for (var yt = 0; yt < MY; yt++) b2.copyWithin(yt * W2, MY * W2, MY * W2 + W2);
    for (var yb = MY + H; yb < H2; yb++) b2.copyWithin(yb * W2, (MY + H - 1) * W2, (MY + H - 1) * W2 + W2);
    if (MX) for (var yr = 0; yr < H2; yr++) {
      var o = yr * W2, v0 = b2[o + MX];
      for (var xc = 0; xc < MX; xc++) b2[o + xc] = v0;
    }

    var r0 = Math.ceil((front.c0 - Y0) / DY), r1 = Math.floor((front.c1 - Y0) / DY);
    var c0 = Math.ceil((front.west - X0) / DX), cE = Math.min(W2 - 1, Math.floor((front.east - X0) / DX));

    /* ---- the sea's own floor, west: the record's first column, where it
       is wet. The far west settles here, so the Precambrian Sea's depth is
       the depth of the sea that continues it. */
    var wv = [];
    for (var ry = r0; ry <= r1; ry++) { var bv = b2[ry * W2 + c0]; if (bv < -0.02) wv.push(bv); }
    wv.sort(function (a, b) { return a - b; });
    var SEA_D = wv.length ? wv[wv.length >> 1] : -0.12;

    /* ==== R39: AND THE FLOOR THE WEST SETTLES ON IS NOT A MEASUREMENT,
       IT IS THE ARITHMETIC ON ZERO COMMITS.

       Tommy, 2026-09-15: "instead of drawing it like early 2022 (where we
       have data points of zero), you drew it as just a flat blue ... all I
       wanted you to do on that side was fix that, so all the other seas
       were equally nicely rendered, with depth going off into infinity to
       the west", and "if you want to literally do it as synthetic data of
       zero commits, so it does the same as 2022, then feel free".

       So: run the record's own chain on a month of zeros. sqrt(0) is 0,
       the sequence smoothing of zeros is zero, buildField of zeros is
       zero, smooth() of zeros is zero, the coast blend takes the low
       branch (0 <= COAST) and leaves zero, and `bed = field - COAST`.
       A zero-commit month of 2022 therefore has bed = -COAST, EXACTLY,
       and no table of synthetic days has to be built to find it out.
       (Measured on this record: COAST 2.1286133, and SEA_D — the median
       of the record's own first wet column — 2.1286132. The record's
       western edge is already open sea at the floor, which is why the two
       agree to seven figures; SEA_D is kept for the rows EAST of the line
       so that side is bit-for-bit what it was.) ==== */
    var FLOOR = -COAST;

    /* ==== V2 (card V2, OPEN 2 of REPORT-room-V1): THE BASIN BEGINS AT THE
       COAST, NOT AT THE RECORD'S WEST EDGE.

       V1 measured `Room.bedAt(-8000, y)` at 0.000 — dry paper — from about
       500 units north of the record's own strip outward, and the boat
       grounds on it, though the fairway west of the harbour claims 2,700
       units of open water either side (REPORT-room-V1 OPEN 2, the same
       family as Tommy's *"i don't understand why i become randomly
       stuck"*).

       WHY. Two rules meet here and the older one wins in the wrong place.
       R3/R31's NORTH-SOUTH rule is the sheet's: the record is a STRIP
       seven rows deep, so the ground either side of it sinks to the paper
       datum over the SINK (637 units) — north of the strip is the sheet's
       margin and margin is paper. R39's WEST rule is the sea's: west of
       January 2022 every row is a zero-commit row, so the bed is FLOOR at
       all of them and the surface is sea and not paper. The two are
       married by `open`, which is measured from `front.west` — the west
       edge of the RECORD. But the voyage does not cast off from the west
       edge of the record: it casts off from the harbour, which stands on
       the record's own first landfall (2022-08-24), and everything west
       of that landfall is already the Precambrian Sea. So between the
       harbour and the record's west edge the sheet's margin rule was
       still sinking the water either side of the strip to paper, and the
       roads she lies in are 1,200 units of that stretch.

       THE FIX, and it is one line of geometry: OUTSIDE THE RECORD'S OWN
       ROWS the basin opens at the first landfall instead. The record's
       own rows (dy = 0) are untouched to the bit, and so is every column
       east of the landfall — the coast east of the harbour, which is the
       whole of the record's drawn shore, cannot move. `0.35` is the same
       threshold voyage.js's `firstLandfall` walks the weeks with, so the
       harbour and the basin's mouth are found by one test and cannot
       disagree. ==== */
    var landCol = -1;
    for (var lc = c0; lc <= cE && landCol < 0; lc++) {
      for (var lr = r0; lr <= r1; lr++) {
        if (b2[lr * W2 + lc] > 0.35) { landCol = lc; break; }
      }
    }
    var BASIN_X = landCol >= 0 ? X0 + landCol * DX : front.west;

    /* ---- (1) WEST: walk the columns out, blurring ALONG the rows ------ */
    var nR = r1 - r0 + 1;
    if (c0 - BEY.SEED > 0) {
      var cur = new Float32Array(nR), nxt = new Float32Array(nR), k, i2;
      var src = c0 - BEY.SEED;
      for (i2 = 0; i2 < nR; i2++) cur[i2] = b2[(r0 + i2) * W2 + src];
      var vN = 0;
      for (k = BEY.SEED + 1; k <= c0; k++) {
        var col = c0 - k; if (col < 0) break;
        /* the spread is isotropic in MAP units, so the radius is in the
           OTHER axis's cells */
        var sg = BEY.BLUR * k * DX / DY, need = sg * sg - vN;   /* JG2: the row's own variance */
        if (need > 1e-6) { blurVar(cur, nxt, nR, need); var t = cur; cur = nxt; nxt = t; vN += need; }
        for (i2 = 0; i2 < nR; i2++) b2[(r0 + i2) * W2 + col] = cur[i2];
      }
    }

    /* ---- (2) NORTH and SOUTH: walk the rows out, blurring ALONG the
       columns — over the WHOLE width, so the north-west corner is the
       west margin continued rather than a quadrant of its own ---------- */
    (function (dir) {
      for (var s = 0; s < 2; s++) {
        var edge = s ? r1 : r0, step = s ? 1 : -1;
        var seed = edge + step * BEY.SEED;
        if (seed < 0 || seed >= H2) continue;
        var cur = new Float32Array(W2), nxt = new Float32Array(W2), i3;
        for (i3 = 0; i3 < W2; i3++) cur[i3] = b2[seed * W2 + i3];
        var vN = 0;
        for (var k2 = BEY.SEED + 1; ; k2++) {
          var row = edge + step * k2; if (row < 0 || row >= H2) break;
          /* BG1 (3): and the blur comes on over a run, not in one row.
             It used to start at its full width (BLUR x 9 rows) on the
             first row past the seed, ~28 map units out: one row of the
             edge's own ground, the next a wide average of it — a step
             in the ground right across the sheet (Tommy's "the land
             suddenly jumps in"). It grows from nothing at the seed at
             the rate it always grew, so it is SEED rows narrower than it
             was all the way out. */
          var sg = BEY.BLUR * (k2 - BEY.SEED) * DY / DX, need = sg * sg - vN;   /* JG2: the row's own variance */
          if (need > 1e-6) { blurVar(cur, nxt, W2, need); var t = cur; cur = nxt; nxt = t; vN += need; }
          b2.set(cur, row * W2);
        }
      }
    })();

    /* ---- (3) AND IT EASES TO THE DATUM, OR TO THE SEA'S OWN FLOOR ---- */
    var FR = BEY.FLAT_RUN * PA, SK = BEY.SINK * PA, OS = BEY.OPEN_SEA * PA, KEEP = 1 - BEY.FLATTEN;
    /* THE FRONT'S OWN LINE — the cut, and the datum east of it */
    var CUT = front.none ? front.west : (front.all ? front.east : front.e1);
    for (var gy = 0; gy < H2; gy++) {
      var my = Y0 + gy * DY, off = gy * W2;
      var dy = Math.max(front.c0 - my, my - front.c1, 0);
      for (var gx = 0; gx < W2; gx++) {
        var mx = X0 + gx * DX;
        var dxW = Math.max(front.west - mx, 0);
        var c = b2[off + gx], out = 0;
        /* (i) THE SURVEY FRONT. The record's own ground is eased out over
           SCARP map units east of the cut and is gone at the foot. What it
           is eased TO is no longer a plane: the shader fades the DOWNS in
           over the very same run (TERRA above, engine.js terraW), so the
           sum of the two is a crossfade from the top of the record to the
           downs and the foot of the scarp lands on ground. A crossfade of
           two values is monotone between them, so nothing dips.
           The array is unchanged here to the bit — the downs are analytic
           and never enter it (see TERRA's note on CLAMP_TO_EDGE), so the
           east margin is still 0 cells and the field's bytes are the
           field's bytes. */
        if (mx > CUT) c *= 1 - smoothstep(CUT, CUT + BEY.SCARP, mx);
        else if (dxW <= 0 && dy <= 0) continue;       // inside the record: untouched
        /* (ii) the three other sides */
        if (dxW > 0 || dy > 0) {
          var dOut = Math.sqrt(dxW * dxW + dy * dy);
          /* BG1 (3): wet by degrees. The test was -0.02..-0.10, so a lake
             carried out past the line held the SEA's full depth while its
             own spread-out water was barely wet, then dropped to paper in
             one row — a cliff a sea deep (#924,473,1018,53,-28). Full sea
             depth is now held only by water at least half that deep, and
             shallower water is pulled toward it by as much as it is wet. */
          var wet = smoothstep(-0.02, Math.min(-0.10, 0.5 * SEA_D), c);
          var open = smoothstep(0, OS, dxW);
          /* NORTH and SOUTH — the rows that have genuinely stopped. Sea
             stays sea at the line and sinks to the paper datum over the
             SINK, which is the crossfade the sheet has always had.
             UNCHANGED, to the float: at dxW = 0 `open` is exactly 0 and
             this is the whole of the old expression. */
          var tgtNS = SEA_D * Math.max(wet, open) * (1 - smoothstep(0, SK, dy));
          /* ==== R39: WEST, THE SEA HAS NO ROWS.
             The old target was `tgtNS` everywhere, so two things went
             wrong west of January 2022: the sink sank the OPEN SEA to
             paper a plate-height north and south of the record's seven
             rows (the pale wedges either side of the west sea), and
             `max(wet, open)` let a shoal at the edge hold a shallower
             floor than the sea it runs into.
             West of the line there are no rows to stop: every row out
             there is a zero-commit row, so the bed is FLOOR at all of
             them, for ever, and the surface north-west of the record is
             sea and not paper. The two rules are married by `open` — the
             same OPEN_SEA run R3 (c) used — so the north/south ease east
             of the line meets the sea west of it over three quarters of a
             plate-height and not on a step; the north-west corner is that
             blend, and it reads as a coast running north out of the
             record's own western shore. ==== */
          /* V2 (OPEN 2): and OUTSIDE the record's own rows the basin's
             mouth is the COAST — see the note beside BASIN_X. Inside them
             (dy = 0) this is `open` itself and the expression is the one
             R39 wrote, to the float. */
          var openB = dy > 0 ? smoothstep(0, OS, Math.max(BASIN_X - mx, 0)) : open;
          if (openB < open) openB = open;
          var tgt = seaWest ? tgtNS + (FLOOR - tgtNS) * openB
                            : SEA_D * Math.max(wet, open) * (1 - smoothstep(0, SK, dy));
          var e = (1 - smoothstep(0, FR, dOut) * KEEP) * (1 - smoothstep(0, SK, dOut));
          c = tgt + (c - tgt) * e;
        }
        b2[off + gx] = c;
      }
    }

    return {
      bed: b2, W: W2, H: H2, seaD: SEA_D, floor: FLOOR, run: RUN, scarp: BEY.SCARP,
      /* V2 (OPEN 2): where the basin's mouth was found — the record's own
         first landfall, which is also where the harbour stands */
      basinX: BASIN_X,
      /* R41: the west gate's run, in MAP units — the same OPEN_SEA the
         ease uses, so the field and engine.js's material turn together */
      openSea: OS,
      /* ==== THE LATTICE'S ORIGIN DOES NOT MOVE. The margin makes the
         array bigger to the north and the west, and the obvious thing —
         move X0/Y0 out with it — costs the fit twice over. The hachure's
         scatter is a hash of the CELL INDEX taken against the origin, so
         moving it re-deals every stroke on the plate (measured: 214,972
         pixels); and `(s.x - X0)/DX` at X0 = -8,915 instead of -8,785 is a
         different float32 rounding, which moves every antialiased edge by
         a fraction of a level (measured: another 4,317).
         So the origin stays the RECORD's, and what moves is the TEXEL
         INDEX: `MX`/`MY` are whole cells, added in `uvOf` after the
         division, which is exact. Every expression that was written
         against the lattice is the expression it was, to the bit. ==== */
      lat: { X0: lat.X0, Y0: lat.Y0, DX: DX, DY: DY, NX: W2, NY: H2,
             MX: MX2, MY: MY2 }
    };
  }

  /* LX1: one row at a time, a monotone cubic through the built samples.
     Output texel j sits at source coordinate j/n (uvOf's texel-centre
     convention carried over: MX scales by n, X0 and DX0 do not move), so
     every n-th output is a built sample to the bit. */
  function upsampleX(MG, n) {
    if (!(n > 1) || !MG || !MG.bed) return MG;
    /* the texture must fit a GPU whose MAX_TEXTURE_SIZE is 8192 (this
       box's software GL is one: at 4x the 9,368-wide upload failed and the
       whole plate came up as bare wash), so the factor gives before the
       width does — 2,342 built goes to 4,684 at 2x and 7,026 at 3x. */
    while (n > 1 && MG.W * n > 8192) n--;
    if (n <= 1) return MG;
    var W = MG.W, H = MG.H, src = MG.bed, W2 = W * n, out = new Float32Array(W2 * H);
    var m = new Float32Array(W), y, x, j;
    for (y = 0; y < H; y++) {
      var r = y * W, o = y * W2;
      /* secants, then limited slopes (|m| <= 3 min|d| either side) */
      for (x = 0; x < W; x++) {
        var dl = x > 0 ? src[r + x] - src[r + x - 1] : 0;
        var dr = x < W - 1 ? src[r + x + 1] - src[r + x] : 0;
        if (x === 0) { m[x] = dr; continue; }
        if (x === W - 1) { m[x] = dl; continue; }
        if (dl * dr <= 0) { m[x] = 0; continue; }
        var s = (dl + dr) * 0.5, lim = 3 * Math.min(Math.abs(dl), Math.abs(dr));
        m[x] = Math.abs(s) > lim ? (s > 0 ? lim : -lim) : s;
      }
      for (j = 0; j < W2; j++) {
        var u = j / n, i0 = Math.min(W - 2, Math.floor(u)), t = u - i0;
        if (t <= 0) { out[o + j] = src[r + i0]; continue; }
        var t2 = t * t, t3 = t2 * t;
        var p0 = src[r + i0], p1 = src[r + i0 + 1];
        out[o + j] = (2 * t3 - 3 * t2 + 1) * p0 + (t3 - 2 * t2 + t) * m[i0]
                   + (-2 * t3 + 3 * t2) * p1 + (t3 - t2) * m[i0 + 1];
      }
    }
    var L = MG.lat;
    var lat2 = { X0: L.X0, Y0: L.Y0, DX: L.DX / n, DY: L.DY, NX: W2, NY: H,
                 MX: (L.MX || 0) * n, MY: L.MY || 0, DX0: L.DX, DY0: L.DY, upx: n };
    var out2 = {}; for (var k in MG) if (Object.prototype.hasOwnProperty.call(MG, k)) out2[k] = MG[k];
    out2.bed = out; out2.W = W2; out2.lat = lat2;
    return out2;
  }

  /* =================================================================
     THE WHOLE STRIP, ONE FIELD.
     record: the home year {year, contributions, today, surveyedTo}
     history: {year: record}         events: {iso: {type,name}}
     horizons: [{date,name,datum?}]  (R36: the marker horizons)
     ================================================================= */
  function build(input) {
    var t0 = (root.performance || Date).now();
    var counts = new Map();
    /* ==== R26: `?ladder=<n>` — THE RECORD, MULTIPLIED, FOR THE CAPTURE
       ONLY. `demo` is x3, the geometry's cap ("3x today's peak"). It
       multiplies the INPUT, so field, contours, lettering, marks,
       section and beyond all see one consistent record; no data file is
       touched and sea level (5 commits, a constant) is not scaled.

       WHY IT TAKES ANY FACTOR: the rung is a CONTOUR LEVEL (lv*lv) over
       the SMOOTHED record, and an isolated peak keeps only 0.402 of its
       own figure — 284 commits on 18 Aug 2026 stand on ground whose top
       level is 114.1, and x3 puts it at 342.2, the same factor. So the
       top brown (125) and the dead 190 rung have never been painted at
       all; today's highest tone in use is #ab6a3e, the 80 rung. A
       PLATEAU keeps its height, and a plateau is the growth Tommy is
       worried about, so the rungs are cut on the record's own figures
       and not on what a spike happens to keep. */
    var LSC = input.ladder === 'demo' ? 3 : (parseFloat(input.ladder) > 0 ? parseFloat(input.ladder) : 1);
    function ingest(rec) { (rec && rec.contributions || []).forEach(function (c) { counts.set(c.date, c.count * LSC); }); }
    Object.keys(input.history || {}).forEach(function (y) { ingest(input.history[y]); });
    ingest(input.record);

    var homeYear = input.record.year;
    var EPOCH = (function (year) {
      var c = new Date(Date.UTC(year, 0, 1)), dow = c.getUTCDay();
      c.setUTCDate(c.getUTCDate() - (C.WEEK_START === 'sunday' ? dow : (dow + 6) % 7));
      c.setUTCHours(0, 0, 0, 0); return c;
    })(homeYear);
    function weekOf(iso) { return Math.floor((dateOf(iso) - EPOCH) / (7 * DAY_MS)); }
    function tOf(iso) { return (dateOf(iso) - EPOCH) / (7 * DAY_MS); }
    function dateAt(w, d) { return isoOf(new Date(EPOCH.getTime() + (w * 7 + d) * DAY_MS)); }

    var today = input.record.today;
    var RECORD = { from: weekOf(C.EARLIEST_DATE), to: weekOf(homeYear + '-12-31') };
    var WEST_EDGE = weekOf(C.EARLIEST_DATE);
    var baseW = RECORD.from - C.CONTEXT_WEEKS, w1 = RECORD.to + C.CONTEXT_WEEKS;
    var nWeeks = w1 - baseW + 1;
    var ux = C.ux, uy = C.uy, pad = C.pad, padY = C.padY;

    function rowOf(wIdx, dIdx) {
      return (C.BOUSTROPHEDON && ((baseW + wIdx) & 1)) ? 6 - dIdx : dIdx;
    }
    var weeks = [];
    for (var w = 0; w < nWeeks; w++) {
      var wk = [];
      for (var d = 0; d < 7; d++) {
        var iso = dateAt(baseW + w, d);
        wk.push({ date: iso, count: counts.get(iso) || 0, inRange: counts.has(iso) });
      }
      weeks.push(wk);
    }

    /* ==== R70: THE LEVELLED DAYS, AND THE LADDER THEY CUT ============
       The record's own working days since the levelling was struck (the
       datum horizon — R36's re-keying, the last day he would have typed).
       TWO NUMBERS come off that one set: its MEDIAN, which the key has
       always printed as the level of a directed day, and its HIGHEST. They
       are the two the ladder is hung on. One pass, one day-set, so the two
       can never be taken from different windows of the record. */
    var LEV = (function () {
      var dh = (input.horizons || []).filter(function (h) { return h.datum; })[0];
      var from = dh ? dh.date : '0000-00-00';
      var BANK = input.bankHolidays || {}, EV = input.events || {}, vals = [];
      for (var lw = 0; lw < nWeeks; lw++) for (var ld = 0; ld < 7; ld++) {
        var dy = weeks[lw][ld];
        if (!dy.inRange || !dy.count) continue;
        if (dy.date <= from || dy.date > today) continue;
        if ((ld + 6) % 7 >= 5) continue;                /* Saturday and Sunday */
        if (BANK[dy.date]) continue;
        var ev = EV[dy.date];
        if (ev && ev.type === 'holiday') continue;
        vals.push(dy.count);
      }
      vals.sort(function (a, b) { return a - b; });
      return { n: vals.length, m: vals.length ? vals[vals.length >> 1] : 0,
               p: vals.length ? vals[vals.length - 1] : 0 };
    })();
    var SCALE = cutLadder(LEV, input.home);
    var LAD = ladder(SCALE);

    /* the record, smoothed along its own sequence (:1837) */
    var seq = [], seqS = [];
    for (var wi = 0; wi < nWeeks; wi++)
      for (var di = 0; di < 7; di++)
        seq.push(weeks[wi][di].inRange ? Math.sqrt(LAD.to(weeks[wi][di].count)) : 0);
    (function () {                       // tomorrow is not a zero (:1852)
      var cut = -1;
      for (var a = 0; a < nWeeks && cut < 0; a++)
        for (var b = 0; b < 7; b++) if (weeks[a][b].date >= today) { cut = a * 7 + b; break; }
      if (cut <= 0) return;
      var last = seq[cut - 1];
      for (var i = cut; i < seq.length; i++) seq[i] = last;
    })();
    (function () {                       // nor the day before the record began (:1884)
      var cut = -1;
      for (var a = 0; a < nWeeks && cut < 0; a++)
        for (var b = 0; b < 7; b++) if (weeks[a][b].date >= C.EARLIEST_DATE) { cut = a * 7 + b; break; }
      if (cut <= 0) return;
      var first = seq[cut];
      for (var i = 0; i < cut; i++) seq[i] = first;
    })();
    for (var si = 0; si < seq.length; si++) {
      var pv = si > 0 ? seq[si - 1] : seq[si];
      var nx = si < seq.length - 1 ? seq[si + 1] : seq[si];
      seqS[si] = seq[si] * (1 - 2 * C.SEQUENCE_SMOOTH) + (pv + nx) * C.SEQUENCE_SMOOTH;
    }
    var grid = new Float32Array(nWeeks * 7);
    for (var gw = 0; gw < nWeeks; gw++)
      for (var gd = 0; gd < 7; gd++)
        grid[rowOf(gw, gd) * nWeeks + gw] = seqS[gw * 7 + gd];

    var base = extendEdges(buildField(grid, nWeeks, 7, ux, uy, pad, padY), 7);
    var field = smooth(cloneField(base), ux, uy * 0.50);
    var coastField = smooth(cloneField(base), ux * 0.55, uy * 0.30);
    var tField = (root.performance || Date).now();

    /* days off are cut out of the ground, exactly (:1934) */
    (function () {
      var EVENTS = input.events || {};
      var leave = new Float32Array(nWeeks * 7), any = false;
      for (var w = 0; w < nWeeks; w++)
        for (var d = 0; d < 7; d++) {
          var day = weeks[w][d], ev = day.inRange && EVENTS[day.date];
          if (ev && ev.type === 'holiday') { leave[rowOf(w, d) * nWeeks + w] = 1; any = true; }
        }
      if (!any) return;
      /* BG1 (3): and built like the ground itself — the edge rows carried
         out past the record's line (extendEdges, as `base` above) before
         the blur. Tommy at #1250,627,1018,51,-25: "I don't massively like
         how this water goes when we get to the edge ... the land suddenly
         jumps in". Without it the cut faded out within ~10 map units of
         the line while the ground it cuts runs on, so uncut ground stood
         up 8 units past the line (the Devon channel's floor -2.13 to -0.3).
         Now a holiday in the edge row runs on as far as the ground does,
         and the margin closes it off. */
      var lf = smooth(extendEdges(buildField(leave, nWeeks, 7, ux, uy, pad, padY), 7), ux * 0.13, uy * 0.13);
      var lo = 0.42, hi = 0.9;
      for (var i = 0; i < lf.data.length; i++) {
        var t = clamp((lf.data[i] - lo) / (hi - lo), 0, 1);
        if (t <= 0) continue;
        var keep = 1 - t * t * (3 - 2 * t);
        field.data[i] *= keep; coastField.data[i] *= keep;
      }
    })();

    /* ==== R36: THE MARKER HORIZONS, AND NO ERAS ======================
       There was one dated line on this sheet and it was an invention —
       Tommy, 2026-09-14: "there is no new vs old series era, that's just
       made up from an arbitrary fault date". What is dated on the ground
       now is the thing that actually happened: a model's release. A
       geologist dates the strata either side of a bed he can recognise
       anywhere he finds it; these are those beds, and nothing on the
       sheet is coloured by which side of one it falls.
       `horizons.json` is the ONE list of dated boundaries — the ages the
       parked design idea would draw are bounded by these and by nothing
       else, so no date is written into the code. The one that carries
       `datum: true` is where the levelling was struck (the last day he
       would have typed); everything else here only needs the date. */
    var horizons = (input.horizons || []).map(function (h) {
      return { date: h.date, name: h.name, datum: !!h.datum, t: tOf(h.date) };
    }).sort(function (a, b) { return a.t - b.t; });
    var datumH = horizons.filter(function (h) { return h.datum; })[0] || null;
    /* in map x, and only where there is field to draw them on */
    var horizonsX = horizons
      .filter(function (h) { return h.t > baseW - 0.5 && h.t < baseW + nWeeks; })
      .map(function (h) { return { x: C.px + h.t * C.cw, date: h.date, name: h.name }; });

    /* ONE SURFACE, NOT TWO (:2010) */
    var COAST = Math.max(0.4, datumLevel(C.BASELINE_COMMITS, ux, uy, pad, padY, ux * 0.55, uy * 0.30));
    (function () {
      var hi = COAST * 2.2;
      for (var i = 0; i < field.data.length; i++) {
        var a = coastField.data[i], b = field.data[i];
        if (a <= COAST) { field.data[i] = a; continue; }
        var t = smoothstep(COAST, hi, a);
        field.data[i] = COAST + (a - COAST) * (1 - t) + Math.max(b - COAST, 0) * t;
      }
    })();

    /* the bed: signed height above the datum, so the coast is the zero
       crossing and the sea has its depth (ROOM-PLAN §1) */
    var bed = new Float32Array(field.data.length), maxH = 0;
    for (var i = 0; i < bed.length; i++) {
      var v = field.data[i] - COAST;
      bed[i] = v; if (v > maxH) maxH = v;
    }

    /* ---- THE THREE SLOPE CUTS, ONCE, ON THE RECORD (ROOM-PLAN §2) --
       The hachure's three Lehmann weights are quantiles of the HOME YEAR's
       own slope population — its own weeks and the eight context weeks
       either side — not of whatever happens to be in the window
       (survey-map.js:2515-2537, v28 A). The shader cannot take a quantile,
       so the three values are computed here, on the same stride-2 lattice
       with the same row stagger, the same floor (mag < 0.0022 is not a
       slope), the same skip below COAST*1.02, and the same conversion of
       the central difference into SHEET units (gx/perWeek, gy/perDay,
       :2026) — which is exactly what the shader's own central difference
       over uLat.z / uLat.w gives it. E1 guessed 0.006 / 0.014 for them. */
    var hach = (function () {
      var W = field.W, H = field.H, d = field.data;
      var perWeek = C.cw / ux, perDay = C.ch / uy;
      var hx0 = pad + (-C.CONTEXT_WEEKS - baseW) * ux;          // the home span's field origin
      var hx1 = pad + (RECORD.to + 1 + C.CONTEXT_WEEKS - baseW) * ux;
      var slopes = [], n = 0;
      for (var fy = padY + 1; fy < H - padY - 1; fy += 2) {
        var stagger = ((fy / 2) | 0) % 2 ? 1 : 0;
        for (var fx = pad + 1 + stagger; fx < W - pad - 1; fx += 2) {
          if (d[fy * W + fx] <= COAST * 1.02) continue;
          var gx = (d[fy * W + fx + 1] - d[fy * W + fx - 1]) / 2 / perWeek;
          var gy = (d[(fy + 1) * W + fx] - d[(fy - 1) * W + fx]) / 2 / perDay;
          var mag = Math.sqrt(gx * gx + gy * gy);
          if (mag < 0.0022) continue;
          n++;
          if (fx >= hx0 && fx < hx1) slopes.push(mag);
        }
      }
      if (!slopes.length) return { cut0: 0.0022, q1: 0.006, q2: 0.014, n: 0, all: n };
      slopes.sort(function (a, b) { return a - b; });
      return {
        cut0: slopes[Math.floor(slopes.length * 0.26)],
        q1: slopes[Math.floor(slopes.length * 0.58)],
        q2: slopes[Math.floor(slopes.length * 0.84)],
        n: slopes.length, all: n
      };
    })();

    /* ---- the front, ANALYTICALLY (ROOM-PLAN §1, cause 3) ---------- */
    var frontW = nWeeks - 1, frontD = 6, found = false;
    for (var a2 = 0; a2 < nWeeks && !found; a2++)
      for (var b2 = 0; b2 < 7; b2++)
        if (weeks[a2][b2].date === today) { frontW = a2; frontD = b2; found = true; break; }
    var allSurveyed = found ? false : (today > weeks[nWeeks - 1][6].date);
    if (!found && !allSurveyed) frontW = -1;

    var alongOf = function (t) { return C.px + t * C.cw; };
    var acrossOf = function (u) { return C.py + (u + C.EDGE_ROWS) * C.ch; };
    var turned = !!(C.BOUSTROPHEDON && ((baseW + frontW) & 1));
    var fr = frontW >= 0 ? rowOf(frontW, frontD) : 0;
    var edge = acrossOf(turned ? fr + 1 : fr);
    var c0 = acrossOf(-C.EDGE_ROWS), c1 = acrossOf(7 + C.EDGE_ROWS);
    var front = {
      all: allSurveyed, none: frontW < 0,
      a0: Math.max(alongOf(baseW), alongOf(WEST_EDGE)),
      e0: alongOf(baseW + frontW), e1: alongOf(baseW + frontW + 1),
      c0: c0, c1: c1,
      pA: turned ? edge : c0, pB: turned ? c1 : edge,
      fade: C.cw * (C.FRONT_FADE_DAYS / 7),
      turned: turned, row: fr,
      east: alongOf(RECORD.to + 1),
      west: alongOf(WEST_EDGE)
    };

    /* the lattice: sheet units <-> field cells (from fxy/proj, :2085) */
    var lat = {
      X0: C.px + C.cw * baseW + (C.cw / ux) * (0.5 - pad),   // sheet x of cell 0
      Y0: C.py + (C.ch / uy) * (0.5 - padY),                 // sheet y of cell 0
      DX: C.cw / ux, DY: C.ch / uy,
      NX: field.W, NY: field.H
    };

    /* THE LETTERING'S PLACEMENT, HERE AND NOT ON THE MAIN THREAD (E3).
       Every name on the plate is placed against the finished field and
       against the ground the symbols have already taken, which is what
       survey-map.js does at build time — so it is done beside the field,
       in the worker, and arrives with it. */
    /* THE MARKS' PLACEMENT (E5), HERE AND NOT ON THE MAIN THREAD, and
       BEFORE the lettering: on the sheet a symbol claims its ground
       first and a name takes what is left (survey-map.js:3735), so the
       reservations are handed on. */
    var markArgs = {
      C: C, weeks: weeks, nWeeks: nWeeks, baseW: baseW, field: field, lat: lat,
      COAST: COAST, front: front, maxHeight: maxH, today: today, RECORD: RECORD,
      events: input.events || {}, bankHolidays: input.bankHolidays || {},
      topo: input.topo || {}, horizons: horizons, tOf: tOf, k: input.k || 1,
      posts: input.posts || [], eventsMode: input.eventsMode || 'name',
      seams: input.seams || '',  /* R9 (b): ?seams=old */
      scale: SCALE               /* R70: the ladder this record is on */
    };
    var marks = root.RoomMarks ? root.RoomMarks.place(markArgs) : null;

    var lettering = root.RoomLettering ? root.RoomLettering.place({
      occupied: marks ? marks.occupied : null,
      C: C, weeks: weeks, nWeeks: nWeeks, baseW: baseW, field: field, lat: lat,
      COAST: COAST, front: front, maxHeight: maxH, today: today, RECORD: RECORD,
      events: input.events || {}, bankHolidays: input.bankHolidays || {},
      topo: input.topo || {}, horizons: horizons, tOf: tOf, k: input.k || 1,
      scale: SCALE                     /* R70: the ladder this record is on */
    }) : null;

    /* ---- THE DAY TABLE (E6) -------------------------------------
       The pick names a day; the card prints its particulars. On the
       sheet both came off the `.sv-hit` rectangles in the SVG — one
       rect per surveyed day, carrying its count, its event and its
       settlement (survey-map.js:4240). The engine has no SVG, so the
       record's own numbers come across with the field: two flat arrays
       indexed [w*7 + d] over the same `weeks` the ground was built
       from, and the epoch the week index is counted from. Events,
       bank holidays and posts are inline JSON in the document and are
       read on the main thread; nothing is duplicated here. */
    /* THE LEVEL OF A DIRECTED DAY — the median working day since the
       datum horizon (survey-map.js newSeriesCommits :1456, re-keyed by
       R36 off the fault and on to the last day he would have typed). The
       card's level line is measured against it, and marks.js settles a
       run against it; it is taken here, once, on the same `weeks` both
       read.
       ==== R70: and it is now the MEDIAN OF THE LEVELLED DAYS, which is
       the same walk over the same days, taken further up this function
       because the ladder the ground is plotted on is hung on it. `LEV`
       is that one pass; this is the number it always was. */
    var newSeries = LEV.n ? LEV.m : C.BASELINE_COMMITS;

    var dayCount = new Int32Array(nWeeks * 7), dayIn = new Uint8Array(nWeeks * 7);
    for (var dw = 0; dw < nWeeks; dw++)
      for (var dd = 0; dd < 7; dd++) {
        dayCount[dw * 7 + dd] = weeks[dw][dd].count;
        dayIn[dw * 7 + dd] = weeks[dw][dd].inRange ? 1 : 0;
      }

    /* R35: and the margin, LAST — after the marks and the names, which are
       placed against the record's own field and must not see the margin,
       and after `bed` is taken, so the record's own values are the ones
       that were there. `?margin=0` is the pair: the field as review 5
       found it. */
    var MG = input.margin === '0'
      ? { bed: bed, W: field.W, H: field.H, lat: lat, run: 0, seaD: 0, floor: 0, scarp: 0, openSea: 0 }
      : continueField(bed, field, lat, front, COAST, input.seawest !== '0');
    /* ==== LX1 (2026-09-21): THE LATTICE IS FINER ACROSS THE SHEET THAN IT
       WAS BUILT. Tommy, of a lake's shore at #924,473,1018,53,-28: "i've
       always wondered why we get this jaggedy line". Flat, that shore is a
       smooth curve; tilted, a sawtooth with THIS lattice's x period (DX =
       80/8 = 10 map units, against DY = 78/22 = 3.5). The ground is the
       lattice sampled bilinearly — C0 tiles with straight edges — so a
       steep scarp running diagonal to it is a saddle per tile, and the
       coast ink, painted on that surface, rides each step. So the finished
       array is upsampled ACROSS, once, here, with a monotone cubic
       (Fritsch-Carlson: it passes through every sample and never
       overshoots, so a flat floor stays flat and no summit grows), and
       everything downstream — the texture, uvOf, day.js bedAt, water.js —
       reads the finer lattice through `lat` exactly as it read the coarse
       one. The marks and the names were placed against the record's own
       field above and are untouched. `?upx=1` is the lattice as built,
       for the pair; `?upx=4` the next octave. The hachure's cell and the
       hill shading's normal key off the BUILT spacing (lat.DX0/DY0,
       engine.js uLat0), so no stroke on the plate is re-dealt. ==== */
    MG = upsampleX(MG, parseInt(input.upx, 10) || 2);

    return {
      lettering: lettering, marks: marks,
      days: { count: dayCount, inr: dayIn, epochMs: EPOCH.getTime(), newSeries: newSeries,
              /* R70: the record's own two numbers, and how many days they
                 were taken off. `peak` is the highest levelled day; the
                 pair is what engine.js hands the worker as `home` when a
                 stranger's record is loaded on to this sheet. */
              peak: LEV.p, levelled: LEV.n,
              /* THE FRONT IS THE END OF YESTERDAY (survey-map.js:6845).
                 Today's cell is paper — the camp stands on it — so the day
                 the survey was carried TO is the last complete one, and
                 that is the day the card reads at rest. */
              surveyedTo: input.record.surveyedTo ||
                isoOf(new Date(dateOf(today).getTime() - DAY_MS)) },
      bed: MG.bed, W: MG.W, H: MG.H, lat: MG.lat, COAST: COAST, maxHeight: maxH,
      scale: SCALE,                    /* R70: null when the sheet is his own */
      margin: { run: MG.run, seaD: MG.seaD, floor: MG.floor, scarp: MG.scarp, basinX: MG.basinX,
                openSea: MG.openSea },
      hach: hach,
      front: front, horizonsX: horizonsX, baseW: baseW, nWeeks: nWeeks, homeYear: homeYear,
      today: today, C: C,
      plate: { x: C.px, y: C.py, w: C.cw * C.weeks, h: C.ch * (7 + C.EDGE_ROWS * 2) },
      sheet: sheetBox(),
      ms: { total: +((root.performance || Date).now() - t0).toFixed(1),
            blur: +(tField - t0).toFixed(1) }
    };
  }

  function sheetBox() {
    var pw = C.cw * C.weeks, ph = C.ch * C.rows;
    var neatInner = { x: C.px - C.gap, y: C.py - C.gap, w: pw + C.gap * 2, h: ph + C.gap * 2 };
    var neatOuter = { x: neatInner.x - C.band, y: neatInner.y - C.band,
                      w: neatInner.w + C.band * 2, h: neatInner.h + C.band * 2 };
    var neatRule = { x: neatOuter.x - 9, y: neatOuter.y - 9, w: neatOuter.w + 18, h: neatOuter.h + 18 };
    var sec = { x: neatOuter.x, y: neatOuter.y + neatOuter.h + 46, w: neatOuter.w, h: 176 };
    var footY = sec.y + sec.h + 40;
    return { w: neatRule.x * 2 + neatRule.w, h: footY + 216 };
  }

  /* ==== R26: THE LADDER, WRITTEN INTO A SHADER =====================
     `TINT_GLSL('tintCol','tintBand')` returns the two functions the
     ground fragment carried by hand; water.js's beyond asks for the
     same pair under its own names. The tones go in as the same
     eight-decimal literals — (n/255).toFixed(8) reproduces them
     character for character — so the shader is byte-identical below the
     new rungs.

     The LEVEL COUNT comes out of the ladder too: the tint is quantised
     to the contour levels (a band edge IS a contour), so a band is only
     reachable if the loop walks a level that high. The old bound of 44
     stopped at level 151. */
  var TINT_STEP = Math.sqrt(5) / 8;
  var TINT_NLEV = 2 * Math.ceil(Math.sqrt(C.TINT_TOP) / TINT_STEP / 2);
  function tintF(h) {
    var n = parseInt(h.slice(1), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  var TINT_RGB = C.TINT_COL.map(tintF);
  function tintVec(i) {
    return 'vec3(' + TINT_RGB[i].map(function (c) { return c.toFixed(8); }).join(',') + ')';
  }
  function TINT_GLSL(colName, bandName) {
    var n = C.TINT_COL.length, r = C.TINT_AT.length, i;
    var s = 'vec3 ' + colName + '(int b){\n';
    for (i = 0; i < n - 1; i++)
      s += '  ' + (i === 0 ? 'if(b<=0)' : 'if(b==' + i + ')') + ' return ' + tintVec(i) + ';\n';
    s += '  return ' + tintVec(n - 1) + ';\n}\n';
    s += 'int ' + bandName + '(float commits){\n  float A[' + r + '];';
    for (i = 0; i < r; i++) s += 'A[' + i + ']=' + C.TINT_AT[i] + '.;';
    s += '\n  int b=1; for(int i=0;i<' + r + ' + uZero;i++){ if(commits>=A[i]) b=i+2; }\n';
    s += '  return min(' + (n - 1) + ',b);\n}\n';
    return s;
  }

  /* THE 190 RUNG, WOKEN. The clamp gives 190 the SAME
     brown as 125, so one band is [125, 300) — 2.4x where every other is
     ~1.5x — exactly where the record is growing into. Waking it cannot
     change the sheet today: the ground's highest level on the filed
     record is 114.1, below the 125 rung (measured: fit diff 0). ON by
     default since the merge. #804b33 is the midpoint of the two browns
     it stands between. */
  function wakeRung190() { C.TINT_COL[9] = '#804b33'; TINT_RGB = C.TINT_COL.map(tintF); exp.TINT_RGB = TINT_RGB; }

  var exp = { build: build, C: C, sheetBox: sheetBox, clamp: clamp, smoothstep: smoothstep,
              fieldAtJS: fieldAtJS,   /* JG1 */
              /* R70 */
              ladder: ladder, LEVEL_FLOOR: LEVEL_FLOOR,
              TINT_GLSL: TINT_GLSL, TINT_RGB: TINT_RGB, TINT_NLEV: TINT_NLEV,
              TINT_STEP: TINT_STEP, wakeRung190: wakeRung190,
              TERRA: TERRA, TERRA_GLSL: TERRA_GLSL, downsJS: downsJS, BEY: BEY,
              /* R46 */
              NOISE_GLSL: NOISE_GLSL, SEABED: SEABED, SEABED_GLSL: SEABED_GLSL,
              seabedJS: seabedJS, WEST: WEST,
              /* X14 (g): the voyage's own ground — the fairway's banks and
                 the marks' foul ground, in the shader and in `Room.bedAt` */
              VOY: VOY, voyGround: VOYG, voyGroundJS: voyGroundJS,
              setVoyFoul: function (list) { VOYG.foul = VOY.OFF ? [] : (list || []); },
              /* V3: the paper's west edge and the berth's own water. The
                 clamp, the voyage's camera and the quay all read these —
                 nobody strikes the date twice. */
              CUT_ISO: CUT_ISO, cutX: cutOf, BERTH_GEOM: VCO, BERTH_VARIANT: BERTHQ,
              /* V3 follow-up: voyage.js stands (c)'s beacon on the point,
                 which is the last capsule of (c)'s own coast. */
              BERTH_SEGS: VCO_SEG };
  root.RoomField = exp;
})(typeof self !== 'undefined' ? self : this);
